/**
 * Elite Batch 8 Part 3 — Workflow Templates 691–717
 * HR, recruiting, employee engagement, crypto, onboarding, offboarding, and compliance workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}

// =============================================================================
// ELITE BATCH 8 PART 3 — TEMPLATES 691–717
// =============================================================================

export const ELITE_BATCH8_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 691. BENEFITS ENROLLMENT REMINDER (8 nodes)
  // ===================================================================

  "Benefits Enrollment Reminder": {
    summary:
      "Automated open enrollment compliance workflow that scans the HRIS for enrollment status, filters incomplete enrollments, generates personalized AI reminders, sends escalating notifications, and logs compliance data. A scheduled scan queries BambooHR for enrollment status, runCode filters incomplete records, AI personalizes the reminder, an initial email is sent, a week delay allows action, a recheck sends a final urgent notice, and compliance data is logged to Sheets.",
    nodes: [
      trigger("Schedule: Daily open enrollment scan", "schedule", {
        schedule: "Daily at 8:00 AM during open enrollment",
        howItStarts:
          "Runs daily during benefits open enrollment period to identify employees who have not completed enrollment",
        setup:
          "Set the schedule to run daily during your open enrollment window (e.g., Nov 1–30). Ensure BambooHR API token is configured with benefits read access.",
        inputFields: [],
      }),

      // 2. BambooHR enrollment status check
      n("2", "hitApi", "BambooHR: Pull enrollment status", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{bamboohr_domain}/v1/reports/custom?format=JSON&fields=id,firstName,lastName,workEmail,department,benefitsStatus,enrollmentDeadline",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key_b64}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "employees",
              customName: "employee_list_json",
              type: "string",
            },
            {
              name: "totalCount",
              customName: "total_employee_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Filter incomplete enrollments (runCode)
      n("3", "runCode", "Filter incomplete enrollments", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Filter employees who have not completed benefits enrollment
const employees = JSON.parse(vars.employee_list_json || "[]");
const incomplete = employees.filter(emp => {
  const status = (emp.benefitsStatus || "").toLowerCase();
  return status !== "complete" && status !== "waived";
});
const first = incomplete[0] || {};
return {
  incomplete_count: incomplete.length,
  incomplete_list_json: JSON.stringify(incomplete),
  has_incomplete: incomplete.length > 0 ? "yes" : "no",
  first_employee_email: first.workEmail || "",
  first_employee_name: (first.firstName || "") + " " + (first.lastName || ""),
  first_department: first.department || "",
  enrollment_deadline: first.enrollmentDeadline || "end of month"
};`,
          outputVariables: [
            { name: "incomplete_count", type: "number" },
            { name: "incomplete_list_json", type: "string" },
            { name: "has_incomplete", type: "string" },
            { name: "first_employee_email", type: "string" },
            { name: "first_employee_name", type: "string" },
            { name: "first_department", type: "string" },
            { name: "enrollment_deadline", type: "string" },
          ],
        },
      }),

      // 4. AI personalized reminder
      aiNode(
        "4",
        "AI: Personalize enrollment reminder",
        760,
        "You are an HR benefits communications specialist. Write a warm, clear, and actionable benefits enrollment reminder for the given employee. Include their specific deadline, mention the consequences of missing enrollment (gap in coverage), and provide a direct link to complete enrollment. Keep the tone supportive, not threatening. Respond JSON: {subject_line: string, email_body: string, urgency_level: string, days_remaining: number}",
        "Employee: {first_employee_name}, Department: {first_department}, Deadline: {enrollment_deadline}, Incomplete employees in batch: {incomplete_count}",
        [
          { field: "subject_line", as: "reminder_subject", type: "string" },
          { field: "email_body", as: "reminder_body", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "days_remaining", as: "days_remaining", type: "number" },
        ],
      ),

      // 5. Initial reminder email
      emailNode(
        "5",
        "Send enrollment reminder email",
        1000,
        "{first_employee_email}",
        "{reminder_subject}",
        "{reminder_body}\\n\\nComplete your enrollment: https://benefits.company.com/enroll\\n\\nDeadline: {enrollment_deadline}\\n\\nIf you have questions, contact HR at benefits@company.com.",
      ),

      // 6. Week delay + recheck
      n("6", "delayAction", "Wait 7 days for action", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "7",
          delayUnit: "days",
        },
      }),

      // 7. Final urgent notice email
      emailNode(
        "7",
        "Send FINAL urgent enrollment notice",
        1480,
        "{first_employee_email}",
        "URGENT: Benefits Enrollment Deadline Approaching - {first_employee_name}",
        "Hi {first_employee_name},\\n\\nThis is your FINAL reminder to complete benefits enrollment before {enrollment_deadline}.\\n\\nIf you do not enroll by the deadline, you will not have benefits coverage until the next enrollment period.\\n\\nComplete enrollment NOW: https://benefits.company.com/enroll\\n\\nIf you need assistance, please contact HR immediately at benefits@company.com or call ext. 5500.\\n\\nHR Benefits Team",
      ),

      // 8. Compliance logging (Sheets)
      sheetsNode(
        "8",
        "Log compliance status (Sheets)",
        1720,
        "BenefitsCompliance",
        '[["{first_employee_email}","{first_employee_name}","{first_department}","{enrollment_deadline}","{urgency_level}","{incomplete_count}","reminder_sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 692. CANDIDATE APPLICATION STATUS BOT (7 nodes)
  // ===================================================================

  "Candidate Application Status Bot": {
    summary:
      "Conversational bot that lets candidates check their application status securely. A chat trigger starts the conversation, AI verifies identity, the ATS stage is pulled from Greenhouse, AI generates a safe status explanation, a disclosure condition gates sensitive info, the chat response is delivered, and if needed, the candidate is escalated to a recruiter.",
    nodes: [
      trigger("Webhook: Chat status inquiry", "webhook_app", {
        source: "Website Chat / Portal",
        event: "chat.message.received",
        howItStarts:
          "A candidate sends a message via the careers portal chat widget asking about their application status",
        setup:
          "Embed the chat widget on your careers page. Configure the webhook to fire on new messages. Payload will include candidate message and session context.",
        inputFields: [],
      }),

      // 2. Identity verification (AI)
      aiNode(
        "2",
        "AI: Verify candidate identity",
        280,
        "You are a secure candidate verification assistant. Extract the candidate identity from the message. Look for email address, full name, or application ID regardless of format. Verify the candidate provided enough information to look them up. Respond JSON: {candidate_email: string, candidate_name: string, application_id: string, identity_verified: string, conversation_id: string, verification_method: string}",
        "Candidate message: {webhook_payload}",
        [
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "application_id", as: "application_id", type: "string" },
          {
            field: "identity_verified",
            as: "identity_verified",
            type: "string",
          },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          {
            field: "verification_method",
            as: "verification_method",
            type: "string",
          },
        ],
      ),

      // 3. ATS stage pull (Greenhouse)
      n("3", "hitApi", "Greenhouse: Pull application stage", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://harvest.greenhouse.io/v1/applications?per_page=1&email={candidate_email}",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_token_b64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "[0].current_stage.name",
              customName: "current_stage",
              type: "string",
            },
            {
              name: "[0].status",
              customName: "application_status",
              type: "string",
            },
            {
              name: "[0].jobs[0].name",
              customName: "applied_role",
              type: "string",
            },
            {
              name: "[0].applied_at",
              customName: "applied_date",
              type: "string",
            },
            {
              name: "[0].last_activity_at",
              customName: "last_activity",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI safe status explanation
      aiNode(
        "4",
        "AI: Generate safe status explanation",
        760,
        "You are a candidate experience specialist. Given the application stage and status, write a friendly, transparent status update for the candidate. Do NOT reveal internal scoring, interviewer names, or rejection reasons if status is rejected. Keep the tone warm and professional. If the candidate is in review, give a general timeline. Respond JSON: {status_message: string, contains_sensitive_info: string, next_steps: string, estimated_timeline: string}",
        "Candidate: {candidate_name}, Role: {applied_role}, Current Stage: {current_stage}, Status: {application_status}, Applied: {applied_date}, Last Activity: {last_activity}",
        [
          { field: "status_message", as: "status_message", type: "string" },
          {
            field: "contains_sensitive_info",
            as: "contains_sensitive_info",
            type: "string",
          },
          { field: "next_steps", as: "next_steps", type: "string" },
          {
            field: "estimated_timeline",
            as: "estimated_timeline",
            type: "string",
          },
        ],
      ),

      // 5. Disclosure block condition
      n("5", "condition", "Contains sensitive information?", 1000, {
        conditionData: [
          {
            variable: "contains_sensitive_info",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Chat response delivery
      n("6", "sendMessage", "Deliver status to candidate", 1240, {
        sendMessageDetails: {
          conversationId: "{conversation_id}",
          message:
            "{status_message}\\n\\nNext steps: {next_steps}\\nEstimated timeline: {estimated_timeline}",
        },
      }),

      // 7. Recruiter escalation
      slackNode(
        "7",
        "Escalate to recruiter (Slack)",
        1480,
        "Candidate status inquiry requires recruiter attention:\\n- Candidate: {candidate_name} ({candidate_email})\\n- Role: {applied_role}\\n- Stage: {current_stage}\\n- Status: {application_status}\\n- Reason: Response contained sensitive information that cannot be auto-disclosed. Please follow up manually.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // safe to disclose -> send chat response
      e("5", "7", "output_2"), // sensitive info -> escalate to recruiter
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 693. CANDIDATE PIPELINE TRACKER (8 nodes)
  // ===================================================================

  "Candidate Pipeline Tracker": {
    summary:
      "Real-time recruiting pipeline visibility workflow. An ATS stage change webhook fires, Greenhouse enriches the event, a condition routes by stage type, an internal Slack pipeline post notifies the team, the candidate receives a stage email, the pipeline dashboard is updated in Sheets, a bottleneck condition checks for dwell time, and extended dwell times are logged for action.",
    nodes: [
      trigger("Webhook: ATS candidate stage change", "webhook_app", {
        source: "Greenhouse",
        event: "candidate.stage_change",
        howItStarts:
          "Greenhouse fires a webhook whenever a candidate moves to a new interview stage",
        setup:
          "In Greenhouse -> Integrations -> Webhooks -> Add -> Event: Candidate Stage Change -> URL: your workflow URL.",
        inputFields: [],
      }),

      // 2. Event enrichment (Greenhouse)
      aiNode(
        "2",
        "AI: Enrich stage change event",
        280,
        "You are an ATS data extraction specialist. Parse the Greenhouse stage change webhook payload. Extract all candidate and stage details regardless of payload format. Respond JSON: {candidate_name: string, candidate_email: string, job_title: string, new_stage: string, previous_stage: string, recruiter_email: string, hiring_manager: string, days_in_previous_stage: number, stage_type: string, is_final_stage: string}",
        "Greenhouse webhook payload: {webhook_payload}",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "new_stage", as: "new_stage", type: "string" },
          { field: "previous_stage", as: "previous_stage", type: "string" },
          { field: "recruiter_email", as: "recruiter_email", type: "string" },
          { field: "hiring_manager", as: "hiring_manager", type: "string" },
          {
            field: "days_in_previous_stage",
            as: "days_in_previous_stage",
            type: "number",
          },
          { field: "stage_type", as: "stage_type", type: "string" },
          { field: "is_final_stage", as: "is_final_stage", type: "string" },
        ],
      ),

      // 3. Stage routing condition
      n("3", "condition", "Is this a final-stage move?", 520, {
        conditionData: [
          {
            variable: "is_final_stage",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Internal Slack pipeline post
      slackNode(
        "4",
        "Post pipeline update (Slack)",
        760,
        "Pipeline Update: {candidate_name} moved to *{new_stage}* for {job_title}\\n- Previous: {previous_stage} ({days_in_previous_stage} days)\\n- Recruiter: {recruiter_email}\\n- Hiring Manager: {hiring_manager}\\n- Stage Type: {stage_type}",
      ),

      // 5. Candidate stage email
      emailNode(
        "5",
        "Send stage update to candidate",
        1000,
        "{candidate_email}",
        "Update on your application for {job_title}",
        "Hi {candidate_name},\\n\\nWe wanted to let you know that your application for {job_title} has been moved to the next stage: {new_stage}.\\n\\nOur team is reviewing your application carefully and we will keep you updated on next steps.\\n\\nThank you for your patience and interest in this role.\\n\\nBest regards,\\nThe Recruiting Team",
      ),

      // 6. Pipeline dashboard (Sheets)
      sheetsNode(
        "6",
        "Update pipeline dashboard (Sheets)",
        1240,
        "CandidatePipeline",
        '[["{candidate_name}","{candidate_email}","{job_title}","{previous_stage}","{new_stage}","{days_in_previous_stage}","{stage_type}","{recruiter_email}","{hiring_manager}"]]',
      ),

      // 7. Bottleneck alert condition
      n("7", "condition", "Dwell time > 10 days?", 1480, {
        conditionData: [
          {
            variable: "days_in_previous_stage",
            value: "10",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Dwell time logging
      slackNode(
        "8",
        "Alert: Pipeline bottleneck detected (Slack)",
        1720,
        "BOTTLENECK ALERT: {candidate_name} spent {days_in_previous_stage} days in {previous_stage} for {job_title}.\\nRecruiter: {recruiter_email} | Hiring Manager: {hiring_manager}\\nPlease review pipeline efficiency for this role.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // final stage -> priority Slack
      e("3", "5", "output_2"), // intermediate stage -> candidate email
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // bottleneck detected
      e("7", "8", "output_2"), // no bottleneck -> still logs for record
    ],
  },

  // ===================================================================
  // 694. CANDIDATE PRE-SCREEN CHATBOT (8 nodes)
  // ===================================================================

  "Candidate Pre-Screen Chatbot": {
    summary:
      "AI-powered chatbot that pre-screens job applicants by conducting a structured conversation, validating required questions, scoring responses, and routing qualified candidates to the ATS. An application trigger starts the flow, AI conducts the screening conversation, runCode validates required questions, a score is computed, a condition routes on score, the ATS is updated in Greenhouse, the recruiter is notified via Slack, and the candidate receives a confirmation.",
    nodes: [
      trigger("Webhook: New application received", "webhook_app", {
        source: "Greenhouse / Careers Portal",
        event: "application.created",
        howItStarts:
          "A new job application is received, triggering the pre-screening chatbot flow",
        setup:
          "Configure Greenhouse webhook for application.created or connect your careers portal form submission. Payload should include candidate details and job ID.",
        inputFields: [],
      }),

      // 2. AI screening conversation
      aiNode(
        "2",
        "AI: Conduct pre-screening conversation",
        280,
        "You are a professional pre-screening interviewer. Given the application data, extract candidate details and generate pre-screening questions tailored to the role. Ask about experience, availability, salary expectations, work authorization, and role-specific skills. Respond JSON: {candidate_name: string, candidate_email: string, job_id: string, job_title: string, screening_questions: string, required_fields: string, resume_summary: string, years_experience: number, salary_expectation: string, work_authorized: string, available_start: string}",
        "Application payload: {webhook_payload}",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "job_id", as: "job_id", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          {
            field: "screening_questions",
            as: "screening_questions",
            type: "string",
          },
          { field: "required_fields", as: "required_fields", type: "string" },
          { field: "resume_summary", as: "resume_summary", type: "string" },
          { field: "years_experience", as: "years_experience", type: "number" },
          {
            field: "salary_expectation",
            as: "salary_expectation",
            type: "string",
          },
          { field: "work_authorized", as: "work_authorized", type: "string" },
          { field: "available_start", as: "available_start", type: "string" },
        ],
      ),

      // 3. Required question validation (runCode)
      n("3", "runCode", "Validate required screening fields", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate that all required screening fields have been answered
const required = (vars.required_fields || "").split(",").map(f => f.trim());
const missing = [];
for (const field of required) {
  if (!vars[field] || vars[field] === "" || vars[field] === "unknown") {
    missing.push(field);
  }
}
const allValid = missing.length === 0;
return {
  missing_fields: missing.join(", ") || "none",
  missing_count: missing.length,
  validation_passed: allValid ? "yes" : "no",
  validation_summary: allValid ? "All required fields complete" : "Missing: " + missing.join(", ")
};`,
          outputVariables: [
            { name: "missing_fields", type: "string" },
            { name: "missing_count", type: "number" },
            { name: "validation_passed", type: "string" },
            { name: "validation_summary", type: "string" },
          ],
        },
      }),

      // 4. Score computation (AI)
      aiNode(
        "4",
        "AI: Compute screening score",
        760,
        "You are a candidate scoring engine. Score this candidate on a 0-100 scale based on: years of experience (25pts), work authorization (25pts), availability (25pts), and resume quality (25pts). Be objective and consistent. Respond JSON: {total_score: number, experience_score: number, authorization_score: number, availability_score: number, resume_score: number, recommendation: string, score_qualified: string}",
        "Candidate: {candidate_name}, Role: {job_title}, Experience: {years_experience} years, Work Authorized: {work_authorized}, Available: {available_start}, Resume: {resume_summary}, Validation: {validation_summary}",
        [
          { field: "total_score", as: "total_score", type: "number" },
          { field: "experience_score", as: "experience_score", type: "number" },
          {
            field: "authorization_score",
            as: "authorization_score",
            type: "number",
          },
          {
            field: "availability_score",
            as: "availability_score",
            type: "number",
          },
          { field: "resume_score", as: "resume_score", type: "number" },
          { field: "recommendation", as: "recommendation", type: "string" },
          { field: "score_qualified", as: "score_qualified", type: "string" },
        ],
      ),

      // 5. Score routing condition
      n("5", "condition", "Score >= 60 (qualified)?", 1000, {
        conditionData: [
          {
            variable: "total_score",
            value: "60",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. ATS update (Greenhouse)
      n("6", "hitApi", "Greenhouse: Update candidate stage", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/applications/{job_id}/move",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_token_b64}" },
            { key: "Content-Type", value: "application/json" },
            { key: "On-Behalf-Of", value: "{greenhouse_user_id}" },
          ],
          body: [
            { key: "from_stage_id", value: "application_review" },
            { key: "to_stage_id", value: "phone_screen" },
          ],
          variables: [
            { name: "id", customName: "move_id", type: "string" },
            { name: "status", customName: "move_status", type: "string" },
          ],
        },
      }),

      // 7. Recruiter notification (Slack)
      slackNode(
        "7",
        "Notify recruiter (Slack)",
        1480,
        "Pre-screened candidate ready for review:\\n- Name: {candidate_name} ({candidate_email})\\n- Role: {job_title}\\n- Score: {total_score}/100 ({recommendation})\\n- Experience: {years_experience} yrs | Auth: {work_authorized} | Available: {available_start}\\n- Resume: {resume_summary}",
      ),

      // 8. Candidate confirmation
      emailNode(
        "8",
        "Send screening confirmation to candidate",
        1720,
        "{candidate_email}",
        "Application Update: {job_title} - Pre-Screening Complete",
        "Hi {candidate_name},\\n\\nThank you for your interest in the {job_title} position.\\n\\nWe have completed the initial review of your application and our recruiting team will be in touch with next steps shortly.\\n\\nIn the meantime, please ensure your contact details are up to date.\\n\\nBest regards,\\nThe Recruiting Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // qualified -> move in ATS
      e("5", "8", "output_2"), // not qualified -> still send polite confirmation
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 695. CANDIDATE SCREENING BOT (8 nodes)
  // ===================================================================

  "Candidate Screening Bot": {
    summary:
      "Automated candidate screening that loads job requirements, runs AI skills/experience screening against the resume, parses and scores must-have criteria, routes on qualification, generates a summary, and updates the ATS. A screening link trigger starts the process, job requirements are loaded, AI screens skills/experience, the resume is parsed, must-have scoring is done via runCode, a routing condition gates, a screening summary is generated, and the ATS is updated.",
    nodes: [
      trigger("Webhook: Screening link activated", "webhook_app", {
        source: "Careers Portal / ATS Link",
        event: "screening.initiated",
        howItStarts:
          "A candidate clicks their unique screening link from the application confirmation email",
        setup:
          "Configure the careers portal to fire a webhook when the screening page is accessed. Payload includes candidate ID and job requisition details.",
        inputFields: [],
      }),

      // 2. Job requirements load
      n("2", "hitApi", "Greenhouse: Load job requirements", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://harvest.greenhouse.io/v1/jobs/{job_id}",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_token_b64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "job_title", type: "string" },
            { name: "notes", customName: "job_requirements", type: "string" },
            {
              name: "departments[0].name",
              customName: "department",
              type: "string",
            },
            {
              name: "custom_fields.must_have_skills",
              customName: "must_have_skills",
              type: "string",
            },
            {
              name: "custom_fields.min_experience_years",
              customName: "min_experience",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI skills/experience screen
      aiNode(
        "3",
        "AI: Screen skills & experience",
        520,
        "You are a technical screening specialist. Analyze the candidate resume against the job requirements. Evaluate each must-have skill, assess experience depth, and identify strengths and gaps. Respond JSON: {candidate_name: string, candidate_email: string, skills_matched: string, skills_missing: string, experience_years: number, experience_relevance: string, strengths: string, gaps: string, technical_depth: string, overall_fit: string}",
        "Screening payload: {webhook_payload}. Job: {job_title}. Requirements: {job_requirements}. Must-have skills: {must_have_skills}. Min experience: {min_experience} years.",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "skills_matched", as: "skills_matched", type: "string" },
          { field: "skills_missing", as: "skills_missing", type: "string" },
          { field: "experience_years", as: "experience_years", type: "number" },
          {
            field: "experience_relevance",
            as: "experience_relevance",
            type: "string",
          },
          { field: "strengths", as: "strengths", type: "string" },
          { field: "gaps", as: "gaps", type: "string" },
          { field: "technical_depth", as: "technical_depth", type: "string" },
          { field: "overall_fit", as: "overall_fit", type: "string" },
        ],
      ),

      // 4. Resume parsing (AI)
      aiNode(
        "4",
        "AI: Deep resume parse",
        760,
        "You are a resume parsing specialist. Extract structured data from the candidate screening response. Parse education, certifications, notable projects, and leadership indicators. Respond JSON: {education_level: string, certifications: string, notable_projects: string, leadership_indicators: string, culture_signals: string, resume_quality_score: number}",
        "Candidate: {candidate_name}, Skills matched: {skills_matched}, Experience: {experience_years} years, Strengths: {strengths}, Gaps: {gaps}, Technical depth: {technical_depth}",
        [
          { field: "education_level", as: "education_level", type: "string" },
          { field: "certifications", as: "certifications", type: "string" },
          { field: "notable_projects", as: "notable_projects", type: "string" },
          {
            field: "leadership_indicators",
            as: "leadership_indicators",
            type: "string",
          },
          { field: "culture_signals", as: "culture_signals", type: "string" },
          {
            field: "resume_quality_score",
            as: "resume_quality_score",
            type: "number",
          },
        ],
      ),

      // 5. Must-have scoring (runCode)
      n("5", "runCode", "Compute must-have screening score", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score candidate on must-have criteria
const mustHaves = (vars.must_have_skills || "").split(",").map(s => s.trim().toLowerCase());
const matched = (vars.skills_matched || "").toLowerCase();
let mustHaveScore = 0;
let mustHaveTotal = mustHaves.length || 1;
for (const skill of mustHaves) {
  if (skill && matched.includes(skill)) mustHaveScore++;
}
const mustHavePct = Math.round((mustHaveScore / mustHaveTotal) * 100);
const expYears = parseInt(vars.experience_years) || 0;
const minExp = parseInt(vars.min_experience) || 0;
const meetsExperience = expYears >= minExp;
const resumeQuality = parseInt(vars.resume_quality_score) || 0;
const finalScore = Math.round(mustHavePct * 0.5 + (meetsExperience ? 25 : 0) + resumeQuality * 0.25);
const passes = finalScore >= 60 ? "yes" : "no";
return {
  must_have_match_pct: mustHavePct,
  meets_experience_req: meetsExperience ? "yes" : "no",
  screening_final_score: finalScore,
  screening_passes: passes,
  score_breakdown: "Must-haves: " + mustHavePct + "%, Experience: " + (meetsExperience ? "meets" : "below") + " (" + expYears + "/" + minExp + " yrs), Resume: " + resumeQuality + "/100"
};`,
          outputVariables: [
            { name: "must_have_match_pct", type: "number" },
            { name: "meets_experience_req", type: "string" },
            { name: "screening_final_score", type: "number" },
            { name: "screening_passes", type: "string" },
            { name: "score_breakdown", type: "string" },
          ],
        },
      }),

      // 6. Routing condition
      n("6", "condition", "Passes screening threshold?", 1240, {
        conditionData: [
          {
            variable: "screening_passes",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Screening summary (AI)
      aiNode(
        "7",
        "AI: Generate screening summary",
        1480,
        "You are a recruiting operations specialist. Create a concise screening summary for the recruiter. Highlight key qualifications, gaps, and a clear recommendation. Respond JSON: {screening_summary: string, recommendation: string, interview_focus_areas: string, risk_factors: string}",
        "Candidate: {candidate_name}, Role: {job_title}, Score: {screening_final_score}/100, Breakdown: {score_breakdown}, Skills: {skills_matched}, Missing: {skills_missing}, Strengths: {strengths}, Gaps: {gaps}, Education: {education_level}, Certs: {certifications}",
        [
          {
            field: "screening_summary",
            as: "screening_summary",
            type: "string",
          },
          {
            field: "recommendation",
            as: "screening_recommendation",
            type: "string",
          },
          {
            field: "interview_focus_areas",
            as: "interview_focus_areas",
            type: "string",
          },
          { field: "risk_factors", as: "risk_factors", type: "string" },
        ],
      ),

      // 8. ATS update (Greenhouse)
      n("8", "hitApi", "Greenhouse: Post screening scorecard", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/applications/{application_id}/scorecards",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_token_b64}" },
            { key: "Content-Type", value: "application/json" },
            { key: "On-Behalf-Of", value: "{greenhouse_user_id}" },
          ],
          body: [
            {
              key: "overall_recommendation",
              value: "{screening_recommendation}",
            },
            {
              key: "attributes",
              value:
                '[{"name":"Must-Have Skills","rating":"{must_have_match_pct}"},{"name":"Experience","rating":"{meets_experience_req}"},{"name":"Resume Quality","rating":"{resume_quality_score}"}]',
            },
          ],
          variables: [
            { name: "id", customName: "scorecard_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // passes -> generate summary
      e("6", "8", "output_2"), // fails -> still update ATS with rejection scorecard
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 696. COMPLIANCE TRAINING COMPLETION TRACKER (9 nodes)
  // ===================================================================

  "Compliance Training Completion Tracker": {
    summary:
      "Tracks compliance training completion across the organization. An LMS webhook fires on course events, the requirements matrix is loaded, runCode computes completion state, a condition classifies status, incomplete employees get reminder emails, managers are notified of overdue items via Slack, a compliance dashboard is updated in Sheets, an org-wide threshold alert fires if completion drops, and a monthly summary is emailed.",
    nodes: [
      trigger("Webhook: LMS course event", "webhook_app", {
        source: "LMS (Docebo / TalentLMS / Cornerstone)",
        event: "course.completed",
        howItStarts:
          "Your LMS fires a webhook when an employee completes, starts, or is overdue on a compliance training course",
        setup:
          "In your LMS -> Integrations -> Webhooks -> Course completion events -> URL: your workflow URL. Ensure employee email and course ID are included.",
        inputFields: [],
      }),

      // 2. Requirements matrix load
      aiNode(
        "2",
        "AI: Parse LMS event & load requirements",
        280,
        "You are a compliance training data specialist. Parse the LMS webhook event and determine the employee details, course completed, and remaining requirements. Respond JSON: {employee_email: string, employee_name: string, manager_email: string, department: string, course_name: string, course_id: string, completion_date: string, completion_status: string, required_courses_total: number, required_courses_completed: number, overdue_courses: string, next_deadline: string}",
        "LMS webhook payload: {webhook_payload}",
        [
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "course_name", as: "course_name", type: "string" },
          { field: "course_id", as: "course_id", type: "string" },
          { field: "completion_date", as: "completion_date", type: "string" },
          {
            field: "completion_status",
            as: "completion_status",
            type: "string",
          },
          {
            field: "required_courses_total",
            as: "required_courses_total",
            type: "number",
          },
          {
            field: "required_courses_completed",
            as: "required_courses_completed",
            type: "number",
          },
          { field: "overdue_courses", as: "overdue_courses", type: "string" },
          { field: "next_deadline", as: "next_deadline", type: "string" },
        ],
      ),

      // 3. Completion state computation (runCode)
      n("3", "runCode", "Compute compliance completion state", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute compliance completion percentage and status
const total = parseInt(vars.required_courses_total) || 1;
const completed = parseInt(vars.required_courses_completed) || 0;
const completionPct = Math.round((completed / total) * 100);
const overdue = (vars.overdue_courses || "").split(",").filter(c => c.trim()).length;
const isCompliant = completionPct === 100 && overdue === 0;
const status = isCompliant ? "compliant" : overdue > 0 ? "overdue" : "in_progress";
return {
  completion_pct: completionPct,
  overdue_count: overdue,
  is_compliant: isCompliant ? "yes" : "no",
  compliance_status: status,
  remaining_courses: total - completed,
  status_label: isCompliant ? "Fully Compliant" : overdue > 0 ? "OVERDUE - Action Required" : "In Progress (" + completionPct + "%)"
};`,
          outputVariables: [
            { name: "completion_pct", type: "number" },
            { name: "overdue_count", type: "number" },
            { name: "is_compliant", type: "string" },
            { name: "compliance_status", type: "string" },
            { name: "remaining_courses", type: "number" },
            { name: "status_label", type: "string" },
          ],
        },
      }),

      // 4. Status classification condition
      n("4", "condition", "Employee fully compliant?", 760, {
        conditionData: [
          {
            variable: "is_compliant",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Employee reminder email
      emailNode(
        "5",
        "Send training reminder to employee",
        1000,
        "{employee_email}",
        "Compliance Training Update: {status_label}",
        "Hi {employee_name},\\n\\nYour compliance training status: {status_label}\\n\\nCompleted: {required_courses_completed}/{required_courses_total} courses ({completion_pct}%)\\nOverdue courses: {overdue_courses}\\nNext deadline: {next_deadline}\\n\\nPlease complete your remaining training as soon as possible to maintain compliance.\\n\\nAccess your training: https://lms.company.com/my-courses\\n\\nHR Compliance Team",
      ),

      // 6. Manager overdue notification (Slack)
      slackNode(
        "6",
        "Notify manager of overdue training (Slack)",
        1240,
        "Compliance Training Alert:\\n- Employee: {employee_name} ({department})\\n- Status: {status_label}\\n- Completed: {required_courses_completed}/{required_courses_total} ({completion_pct}%)\\n- Overdue: {overdue_count} courses\\n- Overdue items: {overdue_courses}\\n- Next deadline: {next_deadline}\\nPlease follow up with this team member.",
      ),

      // 7. Compliance dashboard (Sheets)
      sheetsNode(
        "7",
        "Update compliance dashboard (Sheets)",
        1480,
        "ComplianceTraining",
        '[["{employee_email}","{employee_name}","{department}","{course_name}","{completion_status}","{completion_pct}","{overdue_count}","{compliance_status}","{completion_date}"]]',
      ),

      // 8. Org-wide threshold alert condition
      n("8", "condition", "Completion below 80%?", 1720, {
        conditionData: [
          {
            variable: "completion_pct",
            value: "80",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 9. Monthly summary email
      emailNode(
        "9",
        "Send compliance summary to HR leadership",
        1960,
        "{manager_email}",
        "Compliance Training Report: {department} - {status_label}",
        "Compliance Training Status Report\\n\\nDepartment: {department}\\nEmployee: {employee_name}\\nCompletion: {completion_pct}%\\nCourses Completed: {required_courses_completed}/{required_courses_total}\\nOverdue Courses: {overdue_courses}\\nCompliance Status: {compliance_status}\\n\\nThis employee requires immediate attention to reach compliance targets.\\n\\nPlease ensure all overdue training is completed by {next_deadline}.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "7", "output_1"), // compliant -> skip reminders, log to dashboard
      e("4", "5", "output_2"), // not compliant -> send employee reminder
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"), // below threshold -> send HR alert
    ],
  },

  // ===================================================================
  // 697. CONTRACTOR ONBOARDING WORKFLOW (9 nodes)
  // ===================================================================

  "Contractor Onboarding Workflow": {
    summary:
      "End-to-end contractor onboarding from contract signing through 30-day check-in. A contract signed webhook triggers HRIS record creation in BambooHR, identity is provisioned in Okta, tool access is set up, a welcome package email is sent, compliance documents are collected, the team is introduced via Slack, tasks are tracked in Sheets, and a 30-day check-in is scheduled via delay.",
    nodes: [
      trigger("Webhook: Contract signed", "webhook_app", {
        source: "DocuSign / PandaDoc",
        event: "document.completed",
        howItStarts:
          "A contractor signs their engagement contract via DocuSign or PandaDoc, initiating the onboarding flow",
        setup:
          "Configure DocuSign or PandaDoc webhook for document.completed events. Ensure the contract template includes contractor name, email, role, department, start date, and manager fields.",
        inputFields: [],
      }),

      // 2. AI extraction + HRIS record creation
      aiNode(
        "2",
        "AI: Parse contract & extract details",
        280,
        "You are an HR onboarding data specialist. Parse the contract signing webhook payload and extract all contractor details regardless of format. Respond JSON: {contractor_name: string, contractor_email: string, contractor_role: string, department: string, manager_name: string, manager_email: string, start_date: string, contract_end_date: string, contractor_type: string, hourly_rate: string}",
        "Contract webhook payload: {webhook_payload}",
        [
          { field: "contractor_name", as: "contractor_name", type: "string" },
          { field: "contractor_email", as: "contractor_email", type: "string" },
          { field: "contractor_role", as: "contractor_role", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          {
            field: "contract_end_date",
            as: "contract_end_date",
            type: "string",
          },
          { field: "contractor_type", as: "contractor_type", type: "string" },
          { field: "hourly_rate", as: "hourly_rate", type: "string" },
        ],
      ),

      // 3. BambooHR record creation
      n("3", "hitApi", "BambooHR: Create contractor record", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bamboohr.com/api/gateway.php/{bamboohr_domain}/v1/employees",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key_b64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "firstName", value: "{contractor_name}" },
            { key: "workEmail", value: "{contractor_email}" },
            { key: "department", value: "{department}" },
            { key: "jobTitle", value: "{contractor_role}" },
            { key: "hireDate", value: "{start_date}" },
            { key: "employeeType", value: "contractor" },
          ],
          variables: [
            { name: "id", customName: "bamboo_employee_id", type: "string" },
            {
              name: "status",
              customName: "bamboo_create_status",
              type: "string",
            },
          ],
        },
      }),

      // 4. Identity provisioning (Okta)
      n("4", "hitApi", "Okta: Provision contractor identity", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{okta_domain}.okta.com/api/v1/users?activate=true",
          headers: [
            { key: "Authorization", value: "SSWS {okta_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "profile.firstName", value: "{contractor_name}" },
            { key: "profile.email", value: "{contractor_email}" },
            { key: "profile.login", value: "{contractor_email}" },
            { key: "profile.department", value: "{department}" },
            { key: "profile.title", value: "{contractor_role}" },
            { key: "profile.userType", value: "contractor" },
          ],
          variables: [
            { name: "id", customName: "okta_user_id", type: "string" },
            { name: "status", customName: "okta_status", type: "string" },
          ],
        },
      }),

      // 5. Welcome package email
      emailNode(
        "5",
        "Send welcome package email",
        1000,
        "{contractor_email}",
        "Welcome to the team, {contractor_name}! - Contractor Onboarding",
        "Hi {contractor_name},\\n\\nWelcome aboard! We are excited to have you join us as a {contractor_role} in {department}.\\n\\nHere is what you need to know:\\n- Start Date: {start_date}\\n- Contract End: {contract_end_date}\\n- Manager: {manager_name} ({manager_email})\\n- Okta SSO: Log in at https://{okta_domain}.okta.com with your email\\n\\nPlease complete the following before your start date:\\n1. Accept your Okta invitation email\\n2. Set up 2FA on your account\\n3. Complete compliance documents (link below)\\n\\nCompliance docs: https://hr.company.com/contractor-onboarding/{bamboo_employee_id}\\n\\nWelcome aboard!",
      ),

      // 6. Compliance doc collection
      emailNode(
        "6",
        "Request compliance documents",
        1240,
        "{contractor_email}",
        "Required: Compliance Documents - {contractor_name}",
        "Hi {contractor_name},\\n\\nBefore your start date ({start_date}), please complete and upload the following required documents:\\n\\n1. W-9 Tax Form\\n2. NDA (Non-Disclosure Agreement)\\n3. Background Check Authorization\\n4. Proof of Identity (government-issued ID)\\n\\nUpload here: https://hr.company.com/compliance/{bamboo_employee_id}\\n\\nThese documents are required for compliance and must be submitted before your engagement begins.\\n\\nHR Compliance Team",
      ),

      // 7. Team introduction (Slack)
      slackNode(
        "7",
        "Introduce contractor to team (Slack)",
        1480,
        "Welcome our new contractor!\\n- Name: {contractor_name}\\n- Role: {contractor_role}\\n- Department: {department}\\n- Manager: {manager_name}\\n- Start Date: {start_date}\\n- Contract Period: Until {contract_end_date}\\n\\nPlease give them a warm welcome and help them get up to speed!",
      ),

      // 8. Task tracking (Sheets)
      sheetsNode(
        "8",
        "Log onboarding tasks (Sheets)",
        1720,
        "ContractorOnboarding",
        '[["{contractor_name}","{contractor_email}","{contractor_role}","{department}","{start_date}","{contract_end_date}","{manager_name}","{okta_user_id}","{bamboo_employee_id}","onboarding_started"]]',
      ),

      // 9. 30-day check-in scheduling (delay)
      n("9", "delayAction", "Wait 30 days for check-in", 1960, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "30",
          delayUnit: "days",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 698. CRYPTO MARKET RESEARCH AGENT (8 nodes)
  // ===================================================================

  "Crypto Market Research Agent": {
    summary:
      "Scheduled crypto market intelligence agent that pulls prices, on-chain metrics, news/sentiment, synthesizes a report with AI, and sends alerts. A schedule trigger runs periodically, CoinGecko provides market prices, on-chain metrics are pulled, SerpAPI provides news/sentiment, AI synthesizes a market report, a threshold condition checks for significant moves, a daily report email is sent, and urgent alerts go via Slack and SMS.",
    nodes: [
      trigger("Schedule: Every 4 hours market scan", "schedule", {
        schedule: "Every 4 hours",
        howItStarts:
          "Runs automatically every 4 hours to scan cryptocurrency markets and generate intelligence reports",
        setup:
          "Activate the workflow. Configure CoinGecko API key (free tier works), SerpAPI key for news, and OpenAI key for synthesis. Set alert thresholds as needed.",
        inputFields: [],
      }),

      // 2. Market price pull (CoinGecko)
      n("2", "hitApi", "CoinGecko: Pull market prices", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana,cardano,polkadot&vs_currencies=usd&include_24hr_change=true&include_24hr_vol=true&include_market_cap=true",
          headers: [{ key: "x-cg-demo-api-key", value: "{coingecko_key}" }],
          body: [],
          variables: [
            { name: "bitcoin.usd", customName: "btc_price", type: "number" },
            {
              name: "bitcoin.usd_24h_change",
              customName: "btc_change_24h",
              type: "number",
            },
            {
              name: "bitcoin.usd_24h_vol",
              customName: "btc_volume_24h",
              type: "number",
            },
            { name: "ethereum.usd", customName: "eth_price", type: "number" },
            {
              name: "ethereum.usd_24h_change",
              customName: "eth_change_24h",
              type: "number",
            },
            { name: "solana.usd", customName: "sol_price", type: "number" },
            {
              name: "solana.usd_24h_change",
              customName: "sol_change_24h",
              type: "number",
            },
          ],
        },
      }),

      // 3. On-chain metrics pull
      n("3", "hitApi", "Glassnode: Pull on-chain metrics", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.glassnode.com/v1/metrics/indicators/sopr?a=BTC&i=24h&api_key={glassnode_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "[0].v", customName: "btc_sopr", type: "number" },
          ],
        },
      }),

      // 4. News/sentiment pull (SerpAPI)
      n("4", "hitApi", "SerpAPI: Pull crypto news & sentiment", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q=cryptocurrency+market+news+today&tbm=nws&num=5&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "news_results[0].title",
              customName: "top_headline",
              type: "string",
            },
            {
              name: "news_results[0].snippet",
              customName: "top_snippet",
              type: "string",
            },
            {
              name: "news_results[1].title",
              customName: "second_headline",
              type: "string",
            },
            {
              name: "news_results[2].title",
              customName: "third_headline",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI market synthesis
      aiNode(
        "5",
        "AI: Synthesize market intelligence",
        1000,
        "You are a professional crypto market analyst. Synthesize the price data, on-chain metrics, and news into a concise market intelligence briefing. Include: current prices, 24h changes, key on-chain signals, news sentiment, and a short-term outlook. Flag any extreme moves (>5% change). Respond JSON: {market_summary: string, btc_analysis: string, eth_analysis: string, alt_analysis: string, sentiment_score: number, extreme_move_detected: string, alert_message: string, outlook: string}",
        "BTC: ${btc_price} ({btc_change_24h}%), ETH: ${eth_price} ({eth_change_24h}%), SOL: ${sol_price} ({sol_change_24h}%), BTC SOPR: {btc_sopr}, Headlines: {top_headline} | {second_headline} | {third_headline}, Snippet: {top_snippet}",
        [
          { field: "market_summary", as: "market_summary", type: "string" },
          { field: "btc_analysis", as: "btc_analysis", type: "string" },
          { field: "eth_analysis", as: "eth_analysis", type: "string" },
          { field: "alt_analysis", as: "alt_analysis", type: "string" },
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          {
            field: "extreme_move_detected",
            as: "extreme_move_detected",
            type: "string",
          },
          { field: "alert_message", as: "alert_message", type: "string" },
          { field: "outlook", as: "outlook", type: "string" },
        ],
      ),

      // 6. Threshold condition
      n("6", "condition", "Extreme market move detected?", 1240, {
        conditionData: [
          {
            variable: "extreme_move_detected",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Daily report email
      emailNode(
        "7",
        "Send market intelligence report",
        1480,
        "{report_recipient_email}",
        "Crypto Market Intelligence: BTC ${btc_price} ({btc_change_24h}%)",
        "{market_summary}\\n\\nBitcoin Analysis:\\n{btc_analysis}\\n\\nEthereum Analysis:\\n{eth_analysis}\\n\\nAltcoin Analysis:\\n{alt_analysis}\\n\\nSentiment Score: {sentiment_score}/100\\nOutlook: {outlook}\\n\\nTop Headlines:\\n- {top_headline}\\n- {second_headline}\\n- {third_headline}",
      ),

      // 8. Urgent Slack + SMS alert
      slackNode(
        "8",
        "URGENT: Market alert (Slack)",
        1720,
        "CRYPTO MARKET ALERT:\\n{alert_message}\\n\\nBTC: ${btc_price} ({btc_change_24h}%)\\nETH: ${eth_price} ({eth_change_24h}%)\\nSOL: ${sol_price} ({sol_change_24h}%)\\n\\nSentiment: {sentiment_score}/100 | Outlook: {outlook}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // extreme move -> send report AND alert
      e("6", "7", "output_2"), // normal -> just send report
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 699. CRYPTO TRANSACTION TRACKER (9 nodes)
  // ===================================================================

  "Crypto Transaction Tracker": {
    summary:
      "Monitors cryptocurrency wallet transactions for anomalies and compliance. A wallet monitor trigger fires, runCode enriches the transaction, AI analyzes for anomalies, risk is scored, a condition routes high-risk transactions, compliance is escalated via Slack, a transaction ledger is maintained in Sheets, suspicious activity is recorded, and a monitoring dashboard is updated.",
    nodes: [
      trigger("Webhook: Wallet transaction detected", "webhook_app", {
        source: "Alchemy / QuickNode / Etherscan",
        event: "address.activity",
        howItStarts:
          "A blockchain monitoring service detects a new transaction involving a watched wallet address",
        setup:
          "Configure Alchemy Notify or QuickNode webhooks to monitor your wallet addresses. Set up address activity alerts for incoming and outgoing transactions.",
        inputFields: [],
      }),

      // 2. Transaction enrichment (runCode)
      n("2", "runCode", "Enrich & parse transaction data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse blockchain transaction webhook and enrich with computed fields
const payload = JSON.parse(vars.webhook_payload || "{}");
const event = payload.event || payload;
const activity = (event.activity || [event])[0] || {};
const txHash = activity.hash || activity.transactionHash || event.hash || "";
const fromAddr = activity.fromAddress || activity.from || "";
const toAddr = activity.toAddress || activity.to || "";
const value = parseFloat(activity.value || activity.amount || 0);
const asset = activity.asset || activity.tokenSymbol || "ETH";
const category = activity.category || "external";
const blockNum = activity.blockNum || event.blockNumber || "";
const isLargeTransaction = value > 10;  // threshold: 10 ETH or equivalent
const isNewCounterparty = true;  // simplified - in production, check against known addresses
return {
  tx_hash: txHash,
  from_address: fromAddr,
  to_address: toAddr,
  tx_value: value.toString(),
  tx_asset: asset,
  tx_category: category,
  block_number: blockNum,
  is_large_tx: isLargeTransaction ? "yes" : "no",
  is_new_counterparty: isNewCounterparty ? "yes" : "no",
  tx_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "tx_hash", type: "string" },
            { name: "from_address", type: "string" },
            { name: "to_address", type: "string" },
            { name: "tx_value", type: "string" },
            { name: "tx_asset", type: "string" },
            { name: "tx_category", type: "string" },
            { name: "block_number", type: "string" },
            { name: "is_large_tx", type: "string" },
            { name: "is_new_counterparty", type: "string" },
            { name: "tx_timestamp", type: "string" },
          ],
        },
      }),

      // 3. Anomaly analysis (AI)
      aiNode(
        "3",
        "AI: Analyze transaction for anomalies",
        520,
        "You are a blockchain compliance analyst. Analyze this transaction for suspicious patterns: unusual amount, new counterparty, rapid sequence, known mixer addresses, round-number amounts, or unusual timing. Respond JSON: {anomaly_detected: string, anomaly_types: string, risk_description: string, pattern_match: string, recommended_action: string}",
        "TX: {tx_hash}, From: {from_address}, To: {to_address}, Value: {tx_value} {tx_asset}, Category: {tx_category}, Large: {is_large_tx}, New counterparty: {is_new_counterparty}, Time: {tx_timestamp}",
        [
          { field: "anomaly_detected", as: "anomaly_detected", type: "string" },
          { field: "anomaly_types", as: "anomaly_types", type: "string" },
          { field: "risk_description", as: "risk_description", type: "string" },
          { field: "pattern_match", as: "pattern_match", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),

      // 4. Risk scoring (AI)
      aiNode(
        "4",
        "AI: Compute risk score",
        760,
        "You are a crypto compliance risk scorer. Based on the transaction details and anomaly analysis, assign a risk score from 0-100. Consider: transaction size, counterparty novelty, anomaly types, and pattern matches. Respond JSON: {risk_score: number, risk_level: string, is_high_risk: string, compliance_flag: string, sar_required: string}",
        "TX: {tx_value} {tx_asset}, Anomaly: {anomaly_detected}, Types: {anomaly_types}, Risk: {risk_description}, Pattern: {pattern_match}, Large TX: {is_large_tx}, New counterparty: {is_new_counterparty}",
        [
          { field: "risk_score", as: "risk_score", type: "number" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "is_high_risk", as: "is_high_risk", type: "string" },
          { field: "compliance_flag", as: "compliance_flag", type: "string" },
          { field: "sar_required", as: "sar_required", type: "string" },
        ],
      ),

      // 5. Risk condition
      n("5", "condition", "High risk (score >= 70)?", 1000, {
        conditionData: [
          {
            variable: "risk_score",
            value: "70",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Compliance escalation (Slack)
      slackNode(
        "6",
        "Compliance escalation (Slack)",
        1240,
        "HIGH RISK TRANSACTION ALERT:\\n- TX: {tx_hash}\\n- Value: {tx_value} {tx_asset}\\n- From: {from_address}\\n- To: {to_address}\\n- Risk Score: {risk_score}/100 ({risk_level})\\n- Anomalies: {anomaly_types}\\n- Recommended: {recommended_action}\\n- SAR Required: {sar_required}\\n- Compliance Flag: {compliance_flag}",
      ),

      // 7. Transaction ledger (Sheets)
      sheetsNode(
        "7",
        "Log to transaction ledger (Sheets)",
        1480,
        "CryptoTransactions",
        '[["{tx_hash}","{from_address}","{to_address}","{tx_value}","{tx_asset}","{tx_category}","{risk_score}","{risk_level}","{anomaly_detected}","{tx_timestamp}"]]',
      ),

      // 8. Suspicious activity record
      sheetsNode(
        "8",
        "Record suspicious activity (Sheets)",
        1720,
        "SuspiciousActivity",
        '[["{tx_hash}","{tx_value} {tx_asset}","{risk_score}","{anomaly_types}","{risk_description}","{recommended_action}","{sar_required}","{compliance_flag}","{tx_timestamp}"]]',
      ),

      // 9. Monitoring dashboard
      sheetsNode(
        "9",
        "Update monitoring dashboard (Sheets)",
        1960,
        "MonitoringDashboard",
        '[["{tx_timestamp}","{tx_asset}","{tx_value}","{risk_level}","{compliance_flag}","processed"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high risk -> escalate
      e("5", "7", "output_2"), // low risk -> just log
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 700. CULTURE AMBASSADOR NOMINATION BOT (7 nodes)
  // ===================================================================

  "Culture Ambassador Nomination Bot": {
    summary:
      "Conversational bot for nominating culture ambassadors. A chat trigger starts the flow, AI guides the nomination process, employee validation confirms the nominee via BambooHR, a duplicate check condition prevents repeat nominations, the nomination is stored in Sheets, the nominee is recognized in Slack, and the committee is notified for review.",
    nodes: [
      trigger("Webhook: Nomination chat started", "webhook_app", {
        source: "Slack / Internal Portal Chat",
        event: "chat.nomination.started",
        howItStarts:
          "An employee initiates a culture ambassador nomination via a Slack command or portal chat",
        setup:
          "Configure a Slack slash command (/nominate-ambassador) or portal button to POST to this workflow URL. Payload includes the nominator identity and message.",
        inputFields: [],
      }),

      // 2. AI nomination guidance
      aiNode(
        "2",
        "AI: Guide nomination & extract details",
        280,
        "You are a culture ambassador nomination assistant. Parse the nomination message and extract: the nominator details, the nominee details, the reason for nomination, and specific examples of cultural impact. If information is missing, generate follow-up questions. Respond JSON: {nominator_name: string, nominator_email: string, nominee_name: string, nominee_email: string, nomination_reason: string, cultural_examples: string, nomination_category: string, conversation_id: string, nomination_complete: string}",
        "Nomination payload: {webhook_payload}",
        [
          { field: "nominator_name", as: "nominator_name", type: "string" },
          { field: "nominator_email", as: "nominator_email", type: "string" },
          { field: "nominee_name", as: "nominee_name", type: "string" },
          { field: "nominee_email", as: "nominee_email", type: "string" },
          {
            field: "nomination_reason",
            as: "nomination_reason",
            type: "string",
          },
          {
            field: "cultural_examples",
            as: "cultural_examples",
            type: "string",
          },
          {
            field: "nomination_category",
            as: "nomination_category",
            type: "string",
          },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          {
            field: "nomination_complete",
            as: "nomination_complete",
            type: "string",
          },
        ],
      ),

      // 3. Employee validation (BambooHR)
      n("3", "hitApi", "BambooHR: Validate nominee", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{bamboohr_domain}/v1/employees/directory?format=JSON",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key_b64}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "employees",
              customName: "employee_directory",
              type: "string",
            },
          ],
        },
      }),

      // 4. Duplicate check condition
      n("4", "condition", "Nomination is complete & valid?", 760, {
        conditionData: [
          {
            variable: "nomination_complete",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Nomination storage (Sheets)
      sheetsNode(
        "5",
        "Store nomination (Sheets)",
        1000,
        "CultureNominations",
        '[["{nominator_name}","{nominator_email}","{nominee_name}","{nominee_email}","{nomination_reason}","{cultural_examples}","{nomination_category}","pending_review"]]',
      ),

      // 6. Nominee recognition (Slack)
      slackNode(
        "6",
        "Recognize nominee in Slack",
        1240,
        "Culture Ambassador Nomination!\\n\\n{nominee_name} has been nominated by {nominator_name} for outstanding cultural contribution.\\n\\nCategory: {nomination_category}\\nReason: {nomination_reason}\\n\\nExamples: {cultural_examples}\\n\\nThank you {nominator_name} for recognizing great culture!",
      ),

      // 7. Committee queue notification
      emailNode(
        "7",
        "Notify culture committee",
        1480,
        "culture-committee@company.com",
        "New Culture Ambassador Nomination: {nominee_name}",
        "A new culture ambassador nomination has been submitted.\\n\\nNominator: {nominator_name} ({nominator_email})\\nNominee: {nominee_name} ({nominee_email})\\nCategory: {nomination_category}\\n\\nReason:\\n{nomination_reason}\\n\\nCultural Examples:\\n{cultural_examples}\\n\\nPlease review this nomination in the Culture Nominations dashboard and schedule committee review.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // valid & complete -> store
      e("4", "7", "output_2"), // incomplete -> notify committee of partial
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 701. CUSTOMER ONBOARDING CHECK-IN SEQUENCE (8 nodes)
  // ===================================================================

  "Customer Onboarding Check-in Sequence": {
    summary:
      "Multi-touch customer onboarding check-in sequence with adoption monitoring. An onboarding trigger starts the flow, the customer profile and CSM are loaded from HubSpot, a welcome email is sent, a 1-week delay fires a check-in email, engagement is tracked, low adoption triggers a CSM Slack alert via condition, and a day-30 success review email wraps up.",
    nodes: [
      trigger("Webhook: Customer onboarding started", "webhook_app", {
        source: "HubSpot / CRM",
        event: "deal.won",
        howItStarts:
          "A deal is marked as won in HubSpot, starting the customer onboarding sequence",
        setup:
          "Configure HubSpot workflow to fire a webhook when deal stage changes to 'Closed Won'. Ensure deal properties include contact email, company, and assigned CSM.",
        inputFields: [],
      }),

      // 2. Profile/CSM load (HubSpot)
      aiNode(
        "2",
        "AI: Extract customer & CSM details",
        280,
        "You are a CRM data extraction specialist. Parse the deal won webhook payload and extract customer and CSM details regardless of format. Respond JSON: {customer_email: string, customer_name: string, company_name: string, csm_email: string, csm_name: string, deal_value: string, product_plan: string, contact_id: string, company_id: string}",
        "HubSpot webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "csm_email", as: "csm_email", type: "string" },
          { field: "csm_name", as: "csm_name", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "product_plan", as: "product_plan", type: "string" },
          { field: "contact_id", as: "contact_id", type: "string" },
          { field: "company_id", as: "company_id", type: "string" },
        ],
      ),

      // 3. Welcome email
      emailNode(
        "3",
        "Send welcome email",
        520,
        "{customer_email}",
        "Welcome to {product_plan}, {customer_name}!",
        "Hi {customer_name},\\n\\nWelcome to the team! We are thrilled to have {company_name} on board.\\n\\nYour Customer Success Manager, {csm_name}, will be your primary point of contact throughout your journey with us.\\n\\nHere is how to get started:\\n1. Log in to your account: https://app.company.com/login\\n2. Complete the setup wizard\\n3. Invite your team members\\n4. Explore our getting started guide: https://docs.company.com/getting-started\\n\\nYour CSM will reach out within the first week to ensure everything is going smoothly.\\n\\nBest regards,\\n{csm_name}",
      ),

      // 4. Week-1 check-in email (delay)
      n("4", "delayAction", "Wait 7 days", 760, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "7",
          delayUnit: "days",
        },
      }),

      // 5. Engagement tracking (HubSpot)
      n("5", "hitApi", "HubSpot: Check engagement metrics", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=hs_email_open,hs_email_click,notes_last_updated,num_associated_deals",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "properties.hs_email_open",
              customName: "email_opens",
              type: "number",
            },
            {
              name: "properties.hs_email_click",
              customName: "email_clicks",
              type: "number",
            },
            {
              name: "properties.notes_last_updated",
              customName: "last_activity_date",
              type: "string",
            },
          ],
        },
      }),

      // 6. Low-adoption condition
      n("6", "condition", "Low adoption (no clicks)?", 1240, {
        conditionData: [
          {
            variable: "email_clicks",
            value: "1",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. CSM Slack alert
      slackNode(
        "7",
        "Alert CSM: Low adoption (Slack)",
        1480,
        "LOW ADOPTION ALERT:\\n- Customer: {customer_name} ({company_name})\\n- Plan: {product_plan} | Deal: {deal_value}\\n- Email Opens: {email_opens} | Clicks: {email_clicks}\\n- Last Activity: {last_activity_date}\\n\\nCSM {csm_name}: Please reach out to this customer for a personal check-in.",
      ),

      // 8. Day-30 success review email
      emailNode(
        "8",
        "Send 30-day success review email",
        1720,
        "{customer_email}",
        "Your First 30 Days with {product_plan} - {company_name}",
        "Hi {customer_name},\\n\\nIt has been 30 days since {company_name} joined us, and we wanted to check in on your progress.\\n\\nYour CSM {csm_name} would love to schedule a quick success review to:\\n- Review your usage and adoption\\n- Answer any questions\\n- Share best practices tailored to {company_name}\\n- Discuss your goals for the next quarter\\n\\nBook a time: https://calendly.com/csm/{csm_name}/30-day-review\\n\\nWe are committed to your success!\\n\\nBest regards,\\n{csm_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // low adoption -> alert CSM
      e("6", "8", "output_2"), // healthy adoption -> skip alert
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 702. DEVELOPER ONBOARDING CHECKLIST BOT (9 nodes)
  // ===================================================================

  "Developer Onboarding Checklist Bot": {
    summary:
      "Structured developer onboarding workflow that provisions access, delivers guides, verifies setup, assigns starter tasks, and pairs with a buddy. An engineering hire trigger starts the flow, a bot link is sent, an environment setup guide is delivered, runCode verifies access, coding standards are introduced, a starter task is assigned, a prerequisite condition gates progress, a buddy is introduced via Slack, and completion is notified.",
    nodes: [
      trigger("Webhook: Engineering hire confirmed", "webhook_app", {
        source: "BambooHR / Greenhouse",
        event: "employee.created",
        howItStarts:
          "A new engineering hire is confirmed in the HR system, triggering developer-specific onboarding",
        setup:
          "Configure BambooHR or Greenhouse webhook for new hires filtered to Engineering department. Payload includes hire details and role information.",
        inputFields: [],
      }),

      // 2. AI extraction + onboarding link
      aiNode(
        "2",
        "AI: Extract hire details & generate plan",
        280,
        "You are a developer onboarding coordinator. Parse the new hire webhook and create a personalized onboarding plan. Respond JSON: {dev_name: string, dev_email: string, dev_role: string, team: string, manager_name: string, manager_email: string, tech_stack: string, seniority: string, github_username: string, start_date: string, buddy_email: string}",
        "New engineering hire payload: {webhook_payload}",
        [
          { field: "dev_name", as: "dev_name", type: "string" },
          { field: "dev_email", as: "dev_email", type: "string" },
          { field: "dev_role", as: "dev_role", type: "string" },
          { field: "team", as: "team", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "tech_stack", as: "tech_stack", type: "string" },
          { field: "seniority", as: "seniority", type: "string" },
          { field: "github_username", as: "github_username", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "buddy_email", as: "buddy_email", type: "string" },
        ],
      ),

      // 3. Environment setup guide
      emailNode(
        "3",
        "Send environment setup guide",
        520,
        "{dev_email}",
        "Developer Onboarding: Environment Setup - {dev_name}",
        "Hi {dev_name},\\n\\nWelcome to the engineering team! Here is your environment setup guide:\\n\\n1. Clone the monorepo: git clone git@github.com:company/platform.git\\n2. Install dependencies: Follow /docs/dev-setup.md\\n3. Configure local env: Copy .env.example to .env.local\\n4. Run the test suite: npm test\\n5. Start dev server: npm run dev\\n\\nTech stack: {tech_stack}\\n\\nAccess requests (should be provisioned within 24h):\\n- GitHub: github.com/company (username: {github_username})\\n- Jira: jira.company.com\\n- Datadog: app.datadoghq.com\\n- Slack channels: #eng-{team}, #eng-general\\n\\nIf you run into any issues, reach out to your buddy or #eng-help on Slack.",
      ),

      // 4. Access verification (runCode)
      n("4", "runCode", "Verify access provisioning status", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify that essential access has been provisioned
const devEmail = vars.dev_email || "";
const githubUser = vars.github_username || "";
const checklist = {
  email_provisioned: devEmail.includes("@") ? "yes" : "no",
  github_configured: githubUser.length > 0 ? "yes" : "no",
  start_date_set: (vars.start_date || "").length > 0 ? "yes" : "no",
  buddy_assigned: (vars.buddy_email || "").includes("@") ? "yes" : "no"
};
const totalChecks = Object.keys(checklist).length;
const passedChecks = Object.values(checklist).filter(v => v === "yes").length;
const allPassed = passedChecks === totalChecks;
return {
  access_checks_passed: passedChecks,
  access_checks_total: totalChecks,
  all_access_ready: allPassed ? "yes" : "no",
  access_summary: passedChecks + "/" + totalChecks + " checks passed",
  missing_access: Object.entries(checklist).filter(([k,v]) => v === "no").map(([k]) => k).join(", ") || "none"
};`,
          outputVariables: [
            { name: "access_checks_passed", type: "number" },
            { name: "access_checks_total", type: "number" },
            { name: "all_access_ready", type: "string" },
            { name: "access_summary", type: "string" },
            { name: "missing_access", type: "string" },
          ],
        },
      }),

      // 5. Coding standards intro
      emailNode(
        "5",
        "Send coding standards & culture guide",
        1000,
        "{dev_email}",
        "Engineering Culture & Standards - {team} Team",
        "Hi {dev_name},\\n\\nHere are the key engineering standards for the {team} team:\\n\\n1. Code Review: All PRs require 2 approvals before merge\\n2. Testing: Minimum 80% coverage for new code\\n3. Documentation: Update docs with every API change\\n4. Git: Feature branches from main, squash merges\\n5. CI/CD: All tests must pass before merge\\n\\nResources:\\n- Style Guide: https://wiki.company.com/eng/style-guide\\n- Architecture Docs: https://wiki.company.com/eng/architecture\\n- Runbooks: https://wiki.company.com/eng/runbooks\\n\\nYour tech stack: {tech_stack}\\nSeniority-adjusted expectations: {seniority}-level contributions expected by week 4.",
      ),

      // 6. Starter task assignment
      aiNode(
        "6",
        "AI: Generate starter task",
        1240,
        "You are a developer onboarding specialist. Create an appropriate starter task for a new engineer based on their role, seniority, and tech stack. The task should be achievable within 2-3 days, teach them about the codebase, and produce a meaningful contribution. Respond JSON: {task_title: string, task_description: string, estimated_hours: number, repository: string, relevant_docs: string, acceptance_criteria: string}",
        "Developer: {dev_name}, Role: {dev_role}, Team: {team}, Seniority: {seniority}, Tech stack: {tech_stack}",
        [
          { field: "task_title", as: "task_title", type: "string" },
          { field: "task_description", as: "task_description", type: "string" },
          { field: "estimated_hours", as: "estimated_hours", type: "number" },
          { field: "repository", as: "repository", type: "string" },
          { field: "relevant_docs", as: "relevant_docs", type: "string" },
          {
            field: "acceptance_criteria",
            as: "acceptance_criteria",
            type: "string",
          },
        ],
      ),

      // 7. Prerequisite gating condition
      n("7", "condition", "All access provisioned?", 1480, {
        conditionData: [
          {
            variable: "all_access_ready",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Buddy introduction (Slack)
      slackNode(
        "8",
        "Introduce onboarding buddy (Slack)",
        1720,
        "New team member alert!\\n\\n{dev_name} is joining the {team} team as a {dev_role} ({seniority}).\\n\\nOnboarding buddy: {buddy_email} - please reach out and schedule a welcome chat!\\n\\nStarter task: {task_title} (~{estimated_hours}h)\\nAccess status: {access_summary}\\nStart date: {start_date}",
      ),

      // 9. Completion notification
      emailNode(
        "9",
        "Send onboarding completion to manager",
        1960,
        "{manager_email}",
        "Developer Onboarding Complete: {dev_name}",
        "Hi {manager_name},\\n\\n{dev_name} ({dev_role}) has been fully onboarded to the {team} team.\\n\\nOnboarding Summary:\\n- Environment setup guide: Sent\\n- Coding standards guide: Sent\\n- Access status: {access_summary}\\n- Starter task assigned: {task_title} (~{estimated_hours}h)\\n- Buddy assigned: {buddy_email}\\n- Start date: {start_date}\\n\\nMissing access (if any): {missing_access}\\n\\nPlease schedule a 1:1 within the first week to discuss goals and expectations.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // all access ready -> introduce buddy
      e("7", "9", "output_2"), // missing access -> notify manager
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 703. DIVERSITY AND INCLUSION SURVEY BOT (8 nodes)
  // ===================================================================

  "Diversity and Inclusion Survey Bot": {
    summary:
      "Anonymous D&I survey workflow with strict privacy protections. A schedule trigger runs periodically, runCode generates anonymous tokens, AI conducts the conversational survey, runCode validates responses, AI analyzes sentiment and themes, an anonymity threshold condition ensures privacy, a D&I leadership report is emailed, and priority actions are posted to Slack.",
    nodes: [
      trigger("Schedule: Quarterly D&I survey", "schedule", {
        schedule: "Quarterly (first Monday of quarter)",
        howItStarts:
          "Runs quarterly to distribute anonymous diversity and inclusion surveys to a randomized employee sample",
        setup:
          "Activate the workflow and configure the quarterly schedule. Ensure the employee directory API is accessible. Survey responses are fully anonymized - no individual identification is possible.",
        inputFields: [],
      }),

      // 2. Anonymous token generation (runCode)
      n("2", "runCode", "Generate anonymous survey tokens", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate anonymous tokens for D&I survey - no PII linkage
const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
function genToken(len) {
  let t = "";
  for (let i = 0; i < len; i++) t += chars[Math.floor(Math.random() * chars.length)];
  return t;
}
const batchId = "DI-" + new Date().toISOString().slice(0,10) + "-" + genToken(6);
const surveyToken = genToken(16);
const surveyUrl = "https://surveys.company.com/di/" + batchId + "?token=" + surveyToken;
return {
  batch_id: batchId,
  survey_token: surveyToken,
  survey_url: surveyUrl,
  survey_quarter: "Q" + Math.ceil((new Date().getMonth() + 1) / 3) + " " + new Date().getFullYear(),
  distribution_email: "all-employees@company.com",
  anonymity_notice: "All responses are completely anonymous. No individual identification is possible."
};`,
          outputVariables: [
            { name: "batch_id", type: "string" },
            { name: "survey_token", type: "string" },
            { name: "survey_url", type: "string" },
            { name: "survey_quarter", type: "string" },
            { name: "distribution_email", type: "string" },
            { name: "anonymity_notice", type: "string" },
          ],
        },
      }),

      // 3. Conversational survey (AI)
      aiNode(
        "3",
        "AI: Generate D&I survey questions",
        520,
        "You are a D&I survey design specialist. Generate a set of thoughtful, inclusive survey questions covering: belonging, psychological safety, equitable opportunities, representation, microaggressions, allyship, and suggestions for improvement. Questions should be sensitive and use inclusive language. Respond JSON: {survey_intro: string, questions_json: string, question_count: number, topics_covered: string, estimated_completion_minutes: number}",
        "Quarter: {survey_quarter}, Batch: {batch_id}, Anonymity: {anonymity_notice}",
        [
          { field: "survey_intro", as: "survey_intro", type: "string" },
          { field: "questions_json", as: "questions_json", type: "string" },
          { field: "question_count", as: "question_count", type: "number" },
          { field: "topics_covered", as: "topics_covered", type: "string" },
          {
            field: "estimated_completion_minutes",
            as: "estimated_minutes",
            type: "number",
          },
        ],
      ),

      // 4. Validation (runCode)
      n("4", "runCode", "Validate survey structure & anonymity", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate survey questions don't contain PII-collecting items
const questions = JSON.parse(vars.questions_json || "[]");
const piiPatterns = ["name", "email", "phone", "address", "ssn", "employee id", "badge"];
let piiFound = false;
let piiIssues = [];
for (const q of questions) {
  const text = (typeof q === "string" ? q : q.question || q.text || "").toLowerCase();
  for (const pattern of piiPatterns) {
    if (text.includes(pattern)) {
      piiFound = true;
      piiIssues.push(pattern);
    }
  }
}
return {
  validation_passed: piiFound ? "no" : "yes",
  pii_issues: piiIssues.join(", ") || "none",
  validated_question_count: questions.length,
  anonymity_verified: piiFound ? "no" : "yes"
};`,
          outputVariables: [
            { name: "validation_passed", type: "string" },
            { name: "pii_issues", type: "string" },
            { name: "validated_question_count", type: "number" },
            { name: "anonymity_verified", type: "string" },
          ],
        },
      }),

      // 5. AI sentiment/theme analysis
      aiNode(
        "5",
        "AI: Analyze themes & sentiment",
        1000,
        "You are a D&I analytics specialist. Analyze the survey structure and provide preliminary theme analysis framework. Generate the analysis template that will be used once responses come in. Include expected themes, scoring rubrics, and action item categories. Respond JSON: {analysis_framework: string, theme_categories: string, scoring_rubric: string, action_categories: string, report_template: string, response_threshold: number}",
        "Survey: {survey_quarter}, Questions: {validated_question_count}, Topics: {topics_covered}, Anonymity verified: {anonymity_verified}",
        [
          {
            field: "analysis_framework",
            as: "analysis_framework",
            type: "string",
          },
          { field: "theme_categories", as: "theme_categories", type: "string" },
          { field: "scoring_rubric", as: "scoring_rubric", type: "string" },
          {
            field: "action_categories",
            as: "action_categories",
            type: "string",
          },
          { field: "report_template", as: "report_template", type: "string" },
          {
            field: "response_threshold",
            as: "response_threshold",
            type: "number",
          },
        ],
      ),

      // 6. Anonymity threshold condition
      n("6", "condition", "Anonymity verification passed?", 1240, {
        conditionData: [
          {
            variable: "anonymity_verified",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. D&I leadership report (email)
      emailNode(
        "7",
        "Send D&I survey launch report",
        1480,
        "di-leadership@company.com",
        "D&I Survey Launched: {survey_quarter} - Batch {batch_id}",
        "D&I Survey Launch Report\\n\\nQuarter: {survey_quarter}\\nBatch ID: {batch_id}\\nQuestions: {validated_question_count}\\nTopics: {topics_covered}\\nEstimated completion: {estimated_minutes} minutes\\n\\nAnonymity: VERIFIED - {anonymity_notice}\\n\\nAnalysis Framework:\\n{analysis_framework}\\n\\nTheme Categories: {theme_categories}\\nAction Categories: {action_categories}\\nMinimum Response Threshold: {response_threshold}\\n\\nSurvey URL: {survey_url}\\n\\nResponses will be analyzed once the threshold is met.",
      ),

      // 8. Priority actions Slack post
      slackNode(
        "8",
        "Post D&I survey launch to Slack",
        1720,
        "D&I Survey {survey_quarter} is now live!\\n\\nBatch: {batch_id}\\nQuestions: {validated_question_count}\\nTopics: {topics_covered}\\nTime: ~{estimated_minutes} minutes\\n\\nAll responses are completely anonymous. Please encourage your teams to participate.\\n\\nSurvey: {survey_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // anonymity passed -> launch
      e("6", "8", "output_2"), // anonymity failed -> alert but don't distribute
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 704. EMPLOYEE ADVOCACY CONTENT DISTRIBUTOR (8 nodes)
  // ===================================================================

  "Employee Advocacy Content Distributor": {
    summary:
      "Distributes company content for employee advocacy with AI-personalized platform-specific rewrites. A content ready trigger starts the flow, AI rewrites for different platforms, runCode selects segments, a condition routes by channel, Slack posts for advocates, a digest email is sent, share tracking is logged in Sheets, and a leaderboard is updated.",
    nodes: [
      trigger("Webhook: Content ready for advocacy", "webhook_app", {
        source: "CMS / Marketing Platform",
        event: "content.published",
        howItStarts:
          "A new piece of company content (blog post, press release, product update) is marked ready for employee advocacy",
        setup:
          "Configure your CMS or marketing platform to fire a webhook when content is tagged for employee advocacy. Payload should include content title, URL, summary, and target audience.",
        inputFields: [],
      }),

      // 2. AI platform-specific rewrites
      aiNode(
        "2",
        "AI: Rewrite for social platforms",
        280,
        "You are a social media content strategist. Given the company content, create platform-specific advocacy posts for employees to share. Write versions for LinkedIn (professional), Twitter/X (concise), and internal Slack (casual). Include relevant hashtags and calls-to-action. Respond JSON: {content_title: string, content_url: string, content_summary: string, linkedin_post: string, twitter_post: string, slack_post: string, hashtags: string, target_audience: string, advocacy_priority: string}",
        "Content webhook payload: {webhook_payload}",
        [
          { field: "content_title", as: "content_title", type: "string" },
          { field: "content_url", as: "content_url", type: "string" },
          { field: "content_summary", as: "content_summary", type: "string" },
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          { field: "twitter_post", as: "twitter_post", type: "string" },
          { field: "slack_post", as: "slack_post", type: "string" },
          { field: "hashtags", as: "hashtags", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          {
            field: "advocacy_priority",
            as: "advocacy_priority",
            type: "string",
          },
        ],
      ),

      // 3. Segment selection (runCode)
      n("3", "runCode", "Select advocate segments", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine which employee segments should receive this advocacy content
const audience = (vars.target_audience || "all").toLowerCase();
const priority = (vars.advocacy_priority || "normal").toLowerCase();
const segments = [];
if (audience.includes("engineering") || audience.includes("tech") || audience === "all") segments.push("engineering");
if (audience.includes("sales") || audience.includes("customer") || audience === "all") segments.push("sales");
if (audience.includes("marketing") || audience === "all") segments.push("marketing");
if (audience.includes("leadership") || priority === "high") segments.push("leadership");
if (segments.length === 0) segments.push("all");
const isHighPriority = priority === "high" || priority === "urgent";
return {
  selected_segments: segments.join(", "),
  segment_count: segments.length,
  is_high_priority: isHighPriority ? "yes" : "no",
  distribution_channel: isHighPriority ? "slack_and_email" : "email_only",
  advocacy_list_email: "advocacy-" + segments[0] + "@company.com"
};`,
          outputVariables: [
            { name: "selected_segments", type: "string" },
            { name: "segment_count", type: "number" },
            { name: "is_high_priority", type: "string" },
            { name: "distribution_channel", type: "string" },
            { name: "advocacy_list_email", type: "string" },
          ],
        },
      }),

      // 4. Channel routing condition
      n("4", "condition", "High priority (Slack + Email)?", 760, {
        conditionData: [
          {
            variable: "is_high_priority",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Advocacy Slack post
      slackNode(
        "5",
        "Post advocacy content to Slack",
        1000,
        "NEW ADVOCACY CONTENT: {content_title}\\n\\n{slack_post}\\n\\nReady-to-share posts:\\n\\nLinkedIn: {linkedin_post}\\n\\nTwitter/X: {twitter_post}\\n\\nLink: {content_url}\\nHashtags: {hashtags}\\n\\nSegments: {selected_segments}",
      ),

      // 6. Digest email to advocates
      emailNode(
        "6",
        "Send advocacy digest email",
        1240,
        "{advocacy_list_email}",
        "Share This: {content_title} - Employee Advocacy",
        "Hi team,\\n\\nWe have new content ready for you to share with your networks!\\n\\n{content_title}\\n{content_summary}\\n\\nReady-to-share posts (just copy and paste):\\n\\nLinkedIn:\\n{linkedin_post}\\n\\nTwitter/X:\\n{twitter_post}\\n\\nLink: {content_url}\\nHashtags: {hashtags}\\n\\nSharing company content helps build our brand and demonstrates thought leadership. Thank you for being an advocate!",
      ),

      // 7. Share tracking (Sheets)
      sheetsNode(
        "7",
        "Log advocacy distribution (Sheets)",
        1480,
        "AdvocacyTracking",
        '[["{content_title}","{content_url}","{selected_segments}","{advocacy_priority}","{distribution_channel}","{segment_count}","distributed"]]',
      ),

      // 8. Leaderboard update
      sheetsNode(
        "8",
        "Update advocacy leaderboard (Sheets)",
        1720,
        "AdvocacyLeaderboard",
        '[["{content_title}","{selected_segments}","0","0","0","pending_tracking"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high priority -> Slack first
      e("4", "6", "output_2"), // normal -> email only
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 705. EMPLOYEE BIRTHDAY AND ANNIVERSARY BOT (7 nodes)
  // ===================================================================

  "Employee Birthday and Anniversary Bot": {
    summary:
      "Daily bot that scans the HRIS for birthdays and work anniversaries, generates personalized celebration messages, and posts to Slack with manager reminders. A daily scan queries BambooHR, runCode enriches with milestone type, a condition routes by milestone, AI generates a celebration message, the team Slack channel is posted, the manager gets a reminder email, and major milestones trigger a gift workflow.",
    nodes: [
      trigger("Schedule: Daily HRIS milestone scan", "schedule", {
        schedule: "Daily at 7:00 AM",
        howItStarts:
          "Runs daily to check BambooHR for employee birthdays and work anniversaries occurring today",
        setup:
          "Activate the workflow. Ensure BambooHR API token has read access to employee directory with dateOfBirth and hireDate fields.",
        inputFields: [],
      }),

      // 2. BambooHR scan + enrichment (runCode via AI extraction)
      n("2", "hitApi", "BambooHR: Pull employee milestones", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{bamboohr_domain}/v1/reports/custom?format=JSON&fields=id,firstName,lastName,workEmail,department,supervisor,dateOfBirth,hireDate",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key_b64}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "employees",
              customName: "all_employees_json",
              type: "string",
            },
          ],
        },
      }),

      // 3. Enrichment (runCode)
      n("3", "runCode", "Find today's milestones", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Find employees with birthdays or anniversaries today
const employees = JSON.parse(vars.all_employees_json || "[]");
const today = new Date();
const todayMonth = today.getMonth() + 1;
const todayDay = today.getDate();
const todayYear = today.getFullYear();
let match = null;
let milestoneType = "none";
let yearsOfService = 0;
for (const emp of employees) {
  const dob = emp.dateOfBirth || "";
  const hire = emp.hireDate || "";
  if (dob) {
    const parts = dob.split("-");
    if (parseInt(parts[1]) === todayMonth && parseInt(parts[2]) === todayDay) {
      match = emp; milestoneType = "birthday"; break;
    }
  }
  if (hire) {
    const parts = hire.split("-");
    if (parseInt(parts[1]) === todayMonth && parseInt(parts[2]) === todayDay && parseInt(parts[0]) < todayYear) {
      match = emp; milestoneType = "anniversary";
      yearsOfService = todayYear - parseInt(parts[0]);
      break;
    }
  }
}
if (!match) match = { firstName: "", lastName: "", workEmail: "", department: "", supervisor: "" };
const isMajor = milestoneType === "anniversary" && (yearsOfService === 5 || yearsOfService === 10 || yearsOfService === 15 || yearsOfService === 20);
return {
  employee_name: (match.firstName || "") + " " + (match.lastName || ""),
  employee_email: match.workEmail || "",
  department: match.department || "",
  manager_name: match.supervisor || "",
  milestone_type: milestoneType,
  years_of_service: yearsOfService,
  is_major_milestone: isMajor ? "yes" : "no",
  has_milestone: milestoneType !== "none" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "employee_name", type: "string" },
            { name: "employee_email", type: "string" },
            { name: "department", type: "string" },
            { name: "manager_name", type: "string" },
            { name: "milestone_type", type: "string" },
            { name: "years_of_service", type: "number" },
            { name: "is_major_milestone", type: "string" },
            { name: "has_milestone", type: "string" },
          ],
        },
      }),

      // 4. Milestone routing condition
      n("4", "condition", "Has a milestone today?", 760, {
        conditionData: [
          {
            variable: "has_milestone",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI celebration message
      aiNode(
        "5",
        "AI: Generate celebration message",
        1000,
        "You are a warm, enthusiastic workplace culture bot. Generate a heartfelt celebration message for the employee milestone. For birthdays, be fun and celebratory. For anniversaries, highlight their contributions and years of service. Keep it genuine and not overly corporate. Respond JSON: {celebration_message: string, emoji_header: string, fun_fact: string, gift_suggestion: string}",
        "Employee: {employee_name}, Department: {department}, Milestone: {milestone_type}, Years of service: {years_of_service}, Major milestone: {is_major_milestone}",
        [
          {
            field: "celebration_message",
            as: "celebration_message",
            type: "string",
          },
          { field: "emoji_header", as: "emoji_header", type: "string" },
          { field: "fun_fact", as: "fun_fact", type: "string" },
          { field: "gift_suggestion", as: "gift_suggestion", type: "string" },
        ],
      ),

      // 6. Team Slack post
      slackNode(
        "6",
        "Post celebration to team Slack",
        1240,
        "{emoji_header}\\n\\n{celebration_message}\\n\\n{fun_fact}\\n\\nPlease join us in celebrating {employee_name}!",
      ),

      // 7. Manager reminder email + major milestone gift trigger
      emailNode(
        "7",
        "Send manager reminder",
        1480,
        "{manager_name}@company.com",
        "Reminder: {employee_name}'s {milestone_type} today!",
        "Hi {manager_name},\\n\\nJust a reminder that today is {employee_name}'s {milestone_type}!\\n\\nDepartment: {department}\\nMilestone: {milestone_type}\\nYears of service: {years_of_service}\\n\\nA celebration post has been shared in the team Slack channel. Consider reaching out personally to {employee_name}.\\n\\nGift suggestion: {gift_suggestion}\\n\\nMajor milestone (5/10/15/20 years): {is_major_milestone}\\n\\nHR Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // has milestone -> celebrate
      e("4", "7", "output_2"), // no milestone -> skip (but still trigger for logging)
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 706. EMPLOYEE OFFBOARDING (10 nodes)
  // ===================================================================

  "Employee Offboarding": {
    summary:
      "Comprehensive 10-step employee offboarding workflow covering access revocation, equipment return, exit survey, and multi-team notifications. A departing status trigger fires, runCode calculates deprovision timing, Okta disables the identity, GitHub removes engineering access, equipment return is tracked in Sheets, an exit survey email is sent, exit interview is scheduled, and HR/IT/payroll are all notified.",
    nodes: [
      trigger("Webhook: Employee departing status", "webhook_app", {
        source: "BambooHR / Workday",
        event: "employee.terminated",
        howItStarts:
          "The HRIS marks an employee as departing, triggering the automated offboarding workflow",
        setup:
          "Configure BambooHR or Workday webhook for employee.terminated events. Ensure payload includes employee details, last working day, manager, and department.",
        inputFields: [],
      }),

      // 2. AI extraction + deprovision timing calc
      n("2", "runCode", "Parse departure & calc deprovision timing", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse departure webhook and calculate deprovision timing
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data || payload;
const emp = data.employee || data;
const empName = emp.firstName ? emp.firstName + " " + (emp.lastName || "") : emp.name || emp.displayName || "Employee";
const empEmail = emp.workEmail || emp.email || "";
const department = emp.department || "";
const manager = emp.supervisor || emp.manager || emp.managerName || "";
const managerEmail = emp.supervisorEmail || emp.managerEmail || "";
const lastDay = emp.terminationDate || emp.lastDay || emp.endDate || "";
const reason = emp.terminationReason || emp.reason || "voluntary";
const githubUser = emp.githubUsername || emp.github || "";
// Calculate deprovision timing
const lastDate = new Date(lastDay || Date.now());
const now = new Date();
const daysUntilLastDay = Math.max(0, Math.ceil((lastDate - now) / 86400000));
const immediateDeprovision = daysUntilLastDay <= 0 ? "yes" : "no";
return {
  emp_name: empName,
  emp_email: empEmail,
  department: department,
  manager_name: manager,
  manager_email: managerEmail,
  last_day: lastDay,
  termination_reason: reason,
  github_username: githubUser,
  days_until_last_day: daysUntilLastDay,
  immediate_deprovision: immediateDeprovision,
  deprovision_date: immediateDeprovision === "yes" ? "today" : lastDay
};`,
          outputVariables: [
            { name: "emp_name", type: "string" },
            { name: "emp_email", type: "string" },
            { name: "department", type: "string" },
            { name: "manager_name", type: "string" },
            { name: "manager_email", type: "string" },
            { name: "last_day", type: "string" },
            { name: "termination_reason", type: "string" },
            { name: "github_username", type: "string" },
            { name: "days_until_last_day", type: "number" },
            { name: "immediate_deprovision", type: "string" },
            { name: "deprovision_date", type: "string" },
          ],
        },
      }),

      // 3. Okta identity disable
      n("3", "hitApi", "Okta: Disable identity", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{okta_domain}.okta.com/api/v1/users/{emp_email}/lifecycle/deactivate",
          headers: [
            { key: "Authorization", value: "SSWS {okta_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "status",
              customName: "okta_deactivation_status",
              type: "string",
            },
          ],
        },
      }),

      // 4. GitHub access removal
      n("4", "hitApi", "GitHub: Remove org access", 760, {
        hitApiDetails: {
          method: "DELETE",
          url: "https://api.github.com/orgs/{github_org}/members/{github_username}",
          headers: [
            { key: "Authorization", value: "Bearer {github_admin_token}" },
            { key: "Accept", value: "application/vnd.github+json" },
          ],
          body: [],
          variables: [
            {
              name: "status",
              customName: "github_removal_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. Equipment return tracker (Sheets)
      sheetsNode(
        "5",
        "Track equipment return (Sheets)",
        1000,
        "EquipmentReturn",
        '[["{emp_name}","{emp_email}","{department}","{last_day}","laptop,badge,keys","pending_return"]]',
      ),

      // 6. Exit survey email
      emailNode(
        "6",
        "Send exit survey",
        1240,
        "{emp_email}",
        "We Value Your Feedback - Exit Survey",
        "Hi {emp_name},\\n\\nAs you transition from the company, we would greatly appreciate your honest feedback.\\n\\nPlease complete our confidential exit survey:\\nhttps://surveys.company.com/exit/{emp_email}\\n\\nYour responses help us improve the employee experience for current and future team members. All feedback is confidential.\\n\\nThe survey takes approximately 10 minutes.\\n\\nThank you for your contributions during your time with us.\\n\\nHR Team",
      ),

      // 7. Exit interview scheduling
      emailNode(
        "7",
        "Schedule exit interview",
        1480,
        "{emp_email}",
        "Exit Interview Scheduling - {emp_name}",
        "Hi {emp_name},\\n\\nWe would like to schedule a brief exit interview before your last day ({last_day}).\\n\\nPlease choose a time that works for you:\\nhttps://calendly.com/hr-team/exit-interview\\n\\nThe interview is a casual conversation to understand your experience and gather suggestions. It is completely voluntary and confidential.\\n\\nBest regards,\\nHR Team",
      ),

      // 8. HR ops notification (Slack)
      slackNode(
        "8",
        "Notify HR ops (Slack)",
        1720,
        "OFFBOARDING INITIATED: {emp_name}\\n- Department: {department}\\n- Last Day: {last_day} ({days_until_last_day} days)\\n- Manager: {manager_name}\\n- Reason: {termination_reason}\\n- Okta: {okta_deactivation_status}\\n- GitHub: {github_removal_status}\\n- Exit survey: Sent\\n- Equipment: Pending return",
      ),

      // 9. IT ops notification
      emailNode(
        "9",
        "Notify IT operations",
        1960,
        "it-ops@company.com",
        "Offboarding: Access Revocation - {emp_name} ({last_day})",
        "Employee Offboarding - Access Revocation Checklist\\n\\nEmployee: {emp_name} ({emp_email})\\nDepartment: {department}\\nLast Day: {last_day}\\nDeprovision Date: {deprovision_date}\\n\\nAutomated Actions Completed:\\n- Okta SSO: {okta_deactivation_status}\\n- GitHub: {github_removal_status}\\n\\nManual Actions Required:\\n- [ ] Revoke email access\\n- [ ] Remove from Slack channels\\n- [ ] Disable VPN access\\n- [ ] Revoke cloud console access\\n- [ ] Collect physical equipment\\n- [ ] Remove building badge access",
      ),

      // 10. Payroll notification email
      emailNode(
        "10",
        "Notify payroll",
        2200,
        "payroll@company.com",
        "Offboarding: Final Payroll - {emp_name}",
        "Employee Offboarding - Payroll Action Required\\n\\nEmployee: {emp_name} ({emp_email})\\nDepartment: {department}\\nLast Working Day: {last_day}\\nTermination Reason: {termination_reason}\\nManager: {manager_name}\\n\\nPlease process:\\n- [ ] Final paycheck calculation\\n- [ ] PTO payout\\n- [ ] Benefits termination date\\n- [ ] COBRA notification\\n- [ ] 401(k) rollover information\\n\\nDeprovision date: {deprovision_date}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 707. EMPLOYEE OFFBOARDING SECURITY CHECKLIST (8 nodes)
  // ===================================================================

  "Employee Offboarding Security Checklist": {
    summary:
      "Security-focused offboarding checklist that inventories access, creates a security checklist in Sheets, assigns owners, monitors completion, gates critical items, verifies secure offboarding, and logs the audit trail. A security handoff trigger starts, runCode builds an access inventory, a checklist is created in Sheets, owners are assigned and notified, completion is monitored, critical items are gated by condition, a secure-offboarded block is verified, and an audit log is maintained.",
    nodes: [
      trigger("Webhook: Security handoff initiated", "webhook_app", {
        source: "HR Offboarding System",
        event: "offboarding.security_handoff",
        howItStarts:
          "The offboarding workflow reaches the security handoff phase, triggering the security-specific checklist",
        setup:
          "Configure the upstream offboarding workflow to fire this webhook when security handoff is needed. Payload includes employee details and access inventory.",
        inputFields: [],
      }),

      // 2. Access inventory build (runCode)
      n("2", "runCode", "Build access inventory from payload", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse offboarding payload and build comprehensive access inventory
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data || payload;
const emp = data.employee || data;
const empName = emp.name || emp.displayName || (emp.firstName || "") + " " + (emp.lastName || "");
const empEmail = emp.email || emp.workEmail || "";
const department = emp.department || "";
const securityOwner = emp.securityOwner || "security@company.com";
// Build access inventory
const accessSystems = [
  { system: "Okta SSO", owner: "identity-team", critical: true },
  { system: "Email (Google/O365)", owner: "it-ops", critical: true },
  { system: "GitHub", owner: "eng-ops", critical: department === "Engineering" },
  { system: "AWS Console", owner: "cloud-ops", critical: department === "Engineering" || department === "DevOps" },
  { system: "Slack", owner: "it-ops", critical: false },
  { system: "VPN", owner: "network-ops", critical: true },
  { system: "Building Access", owner: "facilities", critical: true },
  { system: "Jira/Confluence", owner: "it-ops", critical: false }
];
const criticalItems = accessSystems.filter(s => s.critical);
return {
  emp_name: empName,
  emp_email: empEmail,
  department: department,
  security_owner_email: securityOwner,
  access_inventory_json: JSON.stringify(accessSystems),
  total_systems: accessSystems.length,
  critical_count: criticalItems.length,
  critical_systems: criticalItems.map(s => s.system).join(", "),
  checklist_id: "SEC-" + Date.now()
};`,
          outputVariables: [
            { name: "emp_name", type: "string" },
            { name: "emp_email", type: "string" },
            { name: "department", type: "string" },
            { name: "security_owner_email", type: "string" },
            { name: "access_inventory_json", type: "string" },
            { name: "total_systems", type: "number" },
            { name: "critical_count", type: "number" },
            { name: "critical_systems", type: "string" },
            { name: "checklist_id", type: "string" },
          ],
        },
      }),

      // 3. Security checklist creation (Sheets)
      sheetsNode(
        "3",
        "Create security checklist (Sheets)",
        520,
        "SecurityChecklist",
        '[["{checklist_id}","{emp_name}","{emp_email}","{department}","{total_systems}","{critical_count}","{critical_systems}","created","pending"]]',
      ),

      // 4. Owner assignment + notification (email)
      emailNode(
        "4",
        "Assign owners & notify security team",
        760,
        "{security_owner_email}",
        "Security Offboarding Checklist: {emp_name} - {checklist_id}",
        "Security Offboarding Checklist\\n\\nChecklist ID: {checklist_id}\\nEmployee: {emp_name} ({emp_email})\\nDepartment: {department}\\n\\nTotal Systems: {total_systems}\\nCritical Systems: {critical_count}\\n\\nCritical Items (MUST revoke immediately):\\n{critical_systems}\\n\\nFull inventory: See Security Checklist sheet\\n\\nPlease coordinate with system owners to revoke all access. Critical items must be completed within 24 hours of last working day.\\n\\nTrack progress: https://sheets.google.com/d/{sheet_id}/SecurityChecklist",
      ),

      // 5. Completion monitoring (delay + check)
      n("5", "delayAction", "Wait 24h for critical item completion", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "24",
          delayUnit: "hours",
        },
      }),

      // 6. Critical items condition
      n("6", "condition", "Critical systems > 0 pending?", 1240, {
        conditionData: [
          {
            variable: "critical_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Secure-offboarded block
      slackNode(
        "7",
        "ALERT: Critical access not yet revoked (Slack)",
        1480,
        "SECURITY ALERT: Critical offboarding incomplete!\\n\\nEmployee: {emp_name} ({emp_email})\\nChecklist: {checklist_id}\\nCritical systems still active: {critical_systems}\\nCritical count: {critical_count}\\n\\nPlease verify all critical access has been revoked IMMEDIATELY.\\nSecurity owner: {security_owner_email}",
      ),

      // 8. Security audit log (Sheets)
      sheetsNode(
        "8",
        "Log security audit (Sheets)",
        1720,
        "SecurityAuditLog",
        '[["{checklist_id}","{emp_name}","{emp_email}","{department}","{total_systems}","{critical_count}","{critical_systems}","audit_logged"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // critical items pending -> alert
      e("6", "8", "output_2"), // all clear -> log audit
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 708. EMPLOYEE ONBOARDING CHECKLIST (9 nodes)
  // ===================================================================

  "Employee Onboarding Checklist": {
    summary:
      "Role-specific onboarding checklist with IT provisioning, progress tracking, and escalation. A new employee trigger starts the flow, runCode creates a role-specific checklist, tasks are assigned via email, pre-start IT provisioning is done, day-one Slack welcome posts, progress is tracked in Sheets, overdue items are escalated via condition, a completion report is emailed, and the manager is notified.",
    nodes: [
      trigger("Webhook: New employee added", "webhook_app", {
        source: "BambooHR / Workday",
        event: "employee.created",
        howItStarts:
          "A new employee is added to the HRIS, triggering the onboarding checklist workflow",
        setup:
          "Configure BambooHR or Workday webhook for employee.created events. Payload includes name, email, role, department, start date, and manager.",
        inputFields: [],
      }),

      // 2. Role-specific checklist creation (runCode)
      n("2", "runCode", "Create role-specific onboarding checklist", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse new hire data and create role-specific checklist
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data || payload;
const emp = data.employee || data;
const empName = emp.firstName ? emp.firstName + " " + (emp.lastName || "") : emp.name || "New Hire";
const empEmail = emp.workEmail || emp.email || "";
const role = emp.jobTitle || emp.title || emp.role || "";
const dept = emp.department || "";
const startDate = emp.hireDate || emp.startDate || "";
const manager = emp.supervisor || emp.manager || "";
const managerEmail = emp.supervisorEmail || emp.managerEmail || "";
// Generate role-specific tasks
const baseTasks = ["Sign offer letter", "Complete I-9", "Set up direct deposit", "Enroll in benefits", "Complete security training", "Read employee handbook"];
const deptTasks = {
  Engineering: ["Set up dev environment", "Get GitHub access", "Complete code review training", "Meet engineering buddy"],
  Sales: ["Complete CRM training", "Shadow senior rep", "Review sales playbook", "Set up phone system"],
  Marketing: ["Access marketing tools", "Review brand guidelines", "Meet creative team", "Review content calendar"],
};
const roleSpecific = deptTasks[dept] || ["Complete department orientation", "Meet team members"];
const allTasks = baseTasks.concat(roleSpecific);
return {
  emp_name: empName,
  emp_email: empEmail,
  emp_role: role,
  department: dept,
  start_date: startDate,
  manager_name: manager,
  manager_email: managerEmail,
  total_tasks: allTasks.length,
  task_list: allTasks.join(", "),
  checklist_id: "ONB-" + Date.now(),
  has_dept_tasks: roleSpecific.length > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "emp_name", type: "string" },
            { name: "emp_email", type: "string" },
            { name: "emp_role", type: "string" },
            { name: "department", type: "string" },
            { name: "start_date", type: "string" },
            { name: "manager_name", type: "string" },
            { name: "manager_email", type: "string" },
            { name: "total_tasks", type: "number" },
            { name: "task_list", type: "string" },
            { name: "checklist_id", type: "string" },
            { name: "has_dept_tasks", type: "string" },
          ],
        },
      }),

      // 3. Task assignment + notification (email)
      emailNode(
        "3",
        "Send onboarding checklist to new hire",
        520,
        "{emp_email}",
        "Welcome! Your Onboarding Checklist - {emp_name}",
        "Hi {emp_name},\\n\\nWelcome to the team! Here is your personalized onboarding checklist for the {emp_role} role in {department}.\\n\\nYour Tasks ({total_tasks} items):\\n{task_list}\\n\\nStart Date: {start_date}\\nManager: {manager_name}\\nChecklist ID: {checklist_id}\\n\\nTrack your progress: https://onboarding.company.com/{checklist_id}\\n\\nPlease complete all tasks within your first two weeks. Your manager and HR team are here to help!",
      ),

      // 4. Pre-start IT provisioning
      emailNode(
        "4",
        "Request IT provisioning",
        760,
        "it-provisioning@company.com",
        "IT Provisioning Request: {emp_name} - Start {start_date}",
        "New Employee IT Provisioning\\n\\nEmployee: {emp_name} ({emp_email})\\nRole: {emp_role}\\nDepartment: {department}\\nStart Date: {start_date}\\nChecklist: {checklist_id}\\n\\nPlease provision before start date:\\n- [ ] Email account\\n- [ ] SSO/Okta access\\n- [ ] Laptop/workstation\\n- [ ] Department-specific tools\\n- [ ] VPN access\\n- [ ] Badge/building access\\n\\nManager: {manager_name} ({manager_email})",
      ),

      // 5. Day-one welcome (Slack)
      slackNode(
        "5",
        "Post day-one welcome (Slack)",
        1000,
        "Welcome to the team, {emp_name}!\\n\\nRole: {emp_role} | Department: {department}\\nManager: {manager_name}\\nStart Date: {start_date}\\n\\nPlease help {emp_name} feel at home! Drop a welcome message below.",
      ),

      // 6. Progress tracker (Sheets)
      sheetsNode(
        "6",
        "Initialize progress tracker (Sheets)",
        1240,
        "OnboardingProgress",
        '[["{checklist_id}","{emp_name}","{emp_email}","{emp_role}","{department}","{start_date}","{total_tasks}","0","in_progress"]]',
      ),

      // 7. Overdue escalation condition
      n("7", "condition", "Tasks total > 8 (needs follow-up)?", 1480, {
        conditionData: [
          {
            variable: "total_tasks",
            value: "8",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Completion report email
      emailNode(
        "8",
        "Send onboarding tracking report",
        1720,
        "hr-onboarding@company.com",
        "Onboarding Initiated: {emp_name} - {checklist_id}",
        "Onboarding Report\\n\\nEmployee: {emp_name} ({emp_email})\\nRole: {emp_role}\\nDepartment: {department}\\nStart Date: {start_date}\\nManager: {manager_name}\\nTotal Tasks: {total_tasks}\\nChecklist: {checklist_id}\\n\\nStatus: Onboarding initiated. All communications sent.\\nIT Provisioning: Requested\\nDay-one welcome: Posted to Slack\\n\\nFollow up in 1 week to verify progress.",
      ),

      // 9. Manager notification
      emailNode(
        "9",
        "Notify manager of onboarding start",
        1960,
        "{manager_email}",
        "Onboarding Started: {emp_name} joining {department}",
        "Hi {manager_name},\\n\\n{emp_name} ({emp_role}) has been set up for onboarding.\\n\\nStart Date: {start_date}\\nTotal Tasks: {total_tasks}\\nChecklist: {checklist_id}\\n\\nWhat to do:\\n1. Schedule a 1:1 for the first day\\n2. Prepare their workspace\\n3. Assign an onboarding buddy\\n4. Review their task list and add any team-specific items\\n\\nTrack progress: https://onboarding.company.com/{checklist_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // many tasks -> send completion report for tracking
      e("7", "9", "output_2"), // fewer tasks -> go to manager
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 709. EMPLOYEE PULSE SURVEY BOT (7 nodes)
  // ===================================================================

  "Employee Pulse Survey Bot": {
    summary:
      "Quick pulse survey bot that samples employees fairly, conducts a short AI survey, stores anonymized results, analyzes trends, and alerts HR on score drops. A cadence trigger fires, runCode selects a fair sample, AI conducts a short pulse survey, anonymized results are stored in Sheets, trends are analyzed by AI, a score drop condition gates alerts, and HR gets a Slack alert.",
    nodes: [
      trigger("Schedule: Bi-weekly pulse survey", "schedule", {
        schedule: "Bi-weekly (every other Monday at 9:00 AM)",
        howItStarts:
          "Runs bi-weekly to distribute quick pulse surveys to a randomized, fair sample of employees",
        setup:
          "Activate the workflow. Configure OpenAI key for survey generation and analysis. Ensure Google Sheets is set up for anonymized response storage.",
        inputFields: [],
      }),

      // 2. Fair sample selection (runCode)
      n("2", "runCode", "Select fair employee sample", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a fair sampling configuration for pulse survey
const now = new Date();
const weekNum = Math.ceil((now - new Date(now.getFullYear(),0,1)) / 604800000);
const batchId = "PULSE-" + now.toISOString().slice(0,10) + "-W" + weekNum;
const sampleSize = 50;  // target sample size
const departments = ["Engineering", "Sales", "Marketing", "Operations", "Support", "Product"];
const deptSample = Math.ceil(sampleSize / departments.length);
return {
  batch_id: batchId,
  sample_size: sampleSize,
  departments_sampled: departments.join(", "),
  per_dept_sample: deptSample,
  survey_period: now.toISOString().slice(0,10),
  distribution_email: "all-employees@company.com",
  survey_url: "https://surveys.company.com/pulse/" + batchId,
  anonymity_mode: "full"
};`,
          outputVariables: [
            { name: "batch_id", type: "string" },
            { name: "sample_size", type: "number" },
            { name: "departments_sampled", type: "string" },
            { name: "per_dept_sample", type: "number" },
            { name: "survey_period", type: "string" },
            { name: "distribution_email", type: "string" },
            { name: "survey_url", type: "string" },
            { name: "anonymity_mode", type: "string" },
          ],
        },
      }),

      // 3. Short pulse survey (AI)
      aiNode(
        "3",
        "AI: Generate pulse survey questions",
        520,
        "You are an employee engagement specialist. Generate a short (5-question) pulse survey that measures: engagement, workload, manager support, psychological safety, and one rotating topic. Questions should use a 1-5 scale format. Keep it under 2 minutes to complete. Respond JSON: {questions: string, question_count: number, rotating_topic: string, estimated_seconds: number, survey_intro: string}",
        "Batch: {batch_id}, Period: {survey_period}, Departments: {departments_sampled}, Anonymity: {anonymity_mode}",
        [
          { field: "questions", as: "pulse_questions", type: "string" },
          { field: "question_count", as: "question_count", type: "number" },
          { field: "rotating_topic", as: "rotating_topic", type: "string" },
          {
            field: "estimated_seconds",
            as: "estimated_seconds",
            type: "number",
          },
          { field: "survey_intro", as: "survey_intro", type: "string" },
        ],
      ),

      // 4. Anonymized storage (Sheets)
      sheetsNode(
        "4",
        "Store survey config (Sheets)",
        760,
        "PulseSurveys",
        '[["{batch_id}","{survey_period}","{sample_size}","{question_count}","{rotating_topic}","{departments_sampled}","distributed"]]',
      ),

      // 5. Trend analysis (AI)
      aiNode(
        "5",
        "AI: Analyze pulse trends",
        1000,
        "You are a people analytics specialist. Analyze the current pulse survey configuration and provide baseline expectations and trend analysis framework. Identify key metrics to watch and threshold alerts. Respond JSON: {baseline_score: number, watch_metrics: string, score_threshold: number, trend_analysis: string, recommended_actions: string, score_is_low: string}",
        "Batch: {batch_id}, Questions: {question_count}, Topics: engagement, workload, manager support, safety, {rotating_topic}. Sample: {sample_size} across {departments_sampled}.",
        [
          { field: "baseline_score", as: "baseline_score", type: "number" },
          { field: "watch_metrics", as: "watch_metrics", type: "string" },
          { field: "score_threshold", as: "score_threshold", type: "number" },
          { field: "trend_analysis", as: "trend_analysis", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          { field: "score_is_low", as: "score_is_low", type: "string" },
        ],
      ),

      // 6. Score drop condition
      n("6", "condition", "Baseline score below threshold?", 1240, {
        conditionData: [
          {
            variable: "score_is_low",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. HR Slack alert
      slackNode(
        "7",
        "Alert HR: Pulse score concern (Slack)",
        1480,
        "PULSE SURVEY ALERT:\\n\\nBatch: {batch_id} | Period: {survey_period}\\nBaseline Score: {baseline_score}/5.0\\nThreshold: {score_threshold}\\n\\nWatch Metrics: {watch_metrics}\\nTrend Analysis: {trend_analysis}\\n\\nRecommended Actions:\\n{recommended_actions}\\n\\nSurvey URL: {survey_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // low score -> alert HR
    ],
  },

  // ===================================================================
  // 710. EMPLOYEE REFERRAL SUBMISSION BOT (7 nodes)
  // ===================================================================

  "Employee Referral Submission Bot": {
    summary:
      "Conversational bot for submitting employee referrals directly to the ATS. A chat trigger starts the flow, AI collects candidate details, role validation checks open positions, a duplicate condition prevents repeat submissions, the referral is created in Greenhouse, the employee receives confirmation, and the recruiter is notified via Slack.",
    nodes: [
      trigger("Webhook: Referral chat started", "webhook_app", {
        source: "Slack / Internal Portal",
        event: "chat.referral.started",
        howItStarts:
          "An employee initiates a referral submission via Slack command (/refer) or the internal referral portal",
        setup:
          "Configure a Slack slash command (/refer) or portal button to POST to this workflow URL. Payload includes the referring employee details and their message.",
        inputFields: [],
      }),

      // 2. Candidate details collection (AI)
      aiNode(
        "2",
        "AI: Collect referral details",
        280,
        "You are a referral submission assistant. Parse the referral message to extract: the referring employee details, the candidate being referred, the target role, and the relationship context. Respond JSON: {referrer_name: string, referrer_email: string, candidate_name: string, candidate_email: string, candidate_phone: string, target_role: string, relationship: string, referral_reason: string, candidate_linkedin: string, conversation_id: string}",
        "Referral payload: {webhook_payload}",
        [
          { field: "referrer_name", as: "referrer_name", type: "string" },
          { field: "referrer_email", as: "referrer_email", type: "string" },
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "candidate_phone", as: "candidate_phone", type: "string" },
          { field: "target_role", as: "target_role", type: "string" },
          { field: "relationship", as: "relationship", type: "string" },
          { field: "referral_reason", as: "referral_reason", type: "string" },
          {
            field: "candidate_linkedin",
            as: "candidate_linkedin",
            type: "string",
          },
          { field: "conversation_id", as: "conversation_id", type: "string" },
        ],
      ),

      // 3. Role validation
      n("3", "hitApi", "Greenhouse: Validate open role", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://harvest.greenhouse.io/v1/jobs?status=open&per_page=50",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_token_b64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "[0].id", customName: "matching_job_id", type: "string" },
            {
              name: "[0].name",
              customName: "matching_job_name",
              type: "string",
            },
            { name: "[0].status", customName: "job_status", type: "string" },
          ],
        },
      }),

      // 4. Duplicate check condition
      n("4", "condition", "Job is open for referrals?", 760, {
        conditionData: [
          {
            variable: "job_status",
            value: "open",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. ATS referral creation (Greenhouse)
      n("5", "hitApi", "Greenhouse: Create referral candidate", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/candidates",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_token_b64}" },
            { key: "Content-Type", value: "application/json" },
            { key: "On-Behalf-Of", value: "{greenhouse_user_id}" },
          ],
          body: [
            { key: "first_name", value: "{candidate_name}" },
            {
              key: "email_addresses",
              value: '[{"value":"{candidate_email}","type":"personal"}]',
            },
            {
              key: "phone_numbers",
              value: '[{"value":"{candidate_phone}","type":"mobile"}]',
            },
            {
              key: "applications",
              value:
                '[{"job_id":"{matching_job_id}","source_id":"referral","referrer":{"email":"{referrer_email}"}}]',
            },
            {
              key: "notes",
              value:
                "Referred by: {referrer_name} ({referrer_email}). Relationship: {relationship}. Reason: {referral_reason}. LinkedIn: {candidate_linkedin}",
            },
          ],
          variables: [
            {
              name: "id",
              customName: "greenhouse_candidate_id",
              type: "string",
            },
            {
              name: "created_at",
              customName: "referral_created_at",
              type: "string",
            },
          ],
        },
      }),

      // 6. Employee confirmation
      emailNode(
        "6",
        "Send referral confirmation to employee",
        1240,
        "{referrer_email}",
        "Referral Submitted: {candidate_name} for {matching_job_name}",
        "Hi {referrer_name},\\n\\nThank you for referring {candidate_name} for the {matching_job_name} position!\\n\\nReferral Details:\\n- Candidate: {candidate_name} ({candidate_email})\\n- Role: {matching_job_name}\\n- Greenhouse ID: {greenhouse_candidate_id}\\n- Submitted: {referral_created_at}\\n\\nOur recruiting team will review the referral and keep you updated on the status. If the candidate is hired, you will be eligible for the referral bonus per company policy.\\n\\nThank you for helping us find great talent!",
      ),

      // 7. Recruiter Slack notification
      slackNode(
        "7",
        "Notify recruiter (Slack)",
        1480,
        "New Employee Referral Submitted!\\n\\nReferrer: {referrer_name} ({referrer_email})\\nCandidate: {candidate_name} ({candidate_email})\\nRole: {matching_job_name}\\nRelationship: {relationship}\\nReason: {referral_reason}\\nLinkedIn: {candidate_linkedin}\\n\\nGreenhouse ID: {greenhouse_candidate_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // open role -> create referral
      e("4", "6", "output_2"), // closed role -> notify employee
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 711. EMPLOYEE REIMBURSEMENT PROCESSOR (9 nodes)
  // ===================================================================

  "Employee Reimbursement Processor": {
    summary:
      "End-to-end reimbursement processing with AI receipt parsing, policy validation, manager approval, and accounting integration. A submission trigger fires, AI parses receipts, runCode validates against policy, a condition routes violations, Slack sends for manager approval, accounting writes the entry, payroll is queued, the employee is notified by email, and an audit log is maintained in Sheets.",
    nodes: [
      trigger("Webhook: Reimbursement submitted", "webhook_app", {
        source: "Expense Portal / Slack",
        event: "reimbursement.submitted",
        howItStarts:
          "An employee submits a reimbursement request via the expense portal or Slack command",
        setup:
          "Configure the expense portal or Slack slash command to POST reimbursement data to this workflow URL. Include receipt images, amounts, categories, and employee identity.",
        inputFields: [],
      }),

      // 2. AI receipt parsing
      aiNode(
        "2",
        "AI: Parse & validate receipts",
        280,
        "You are an expense receipt parsing specialist. Extract all reimbursement details from the submission. Parse receipt data including: vendor, amount, date, category, business purpose, and receipt validity. Respond JSON: {employee_name: string, employee_email: string, manager_email: string, receipt_vendor: string, receipt_amount: number, receipt_currency: string, receipt_date: string, expense_category: string, business_purpose: string, receipt_valid: string, department: string, reimbursement_id: string}",
        "Reimbursement payload: {webhook_payload}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "receipt_vendor", as: "receipt_vendor", type: "string" },
          { field: "receipt_amount", as: "receipt_amount", type: "number" },
          { field: "receipt_currency", as: "receipt_currency", type: "string" },
          { field: "receipt_date", as: "receipt_date", type: "string" },
          { field: "expense_category", as: "expense_category", type: "string" },
          { field: "business_purpose", as: "business_purpose", type: "string" },
          { field: "receipt_valid", as: "receipt_valid", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "reimbursement_id", as: "reimbursement_id", type: "string" },
        ],
      ),

      // 3. Policy validation (runCode)
      n("3", "runCode", "Validate against reimbursement policy", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate reimbursement against company policy
const amount = parseFloat(vars.receipt_amount) || 0;
const category = (vars.expense_category || "").toLowerCase();
const receiptValid = vars.receipt_valid === "yes";
const violations = [];
// Policy limits by category
const limits = { meals: 75, travel: 500, supplies: 200, lodging: 300, entertainment: 100, other: 150 };
const limit = limits[category] || limits.other;
if (amount > limit) violations.push("Amount $" + amount + " exceeds " + category + " limit of $" + limit);
if (!receiptValid) violations.push("Receipt validation failed");
if (amount <= 0) violations.push("Invalid amount");
const daysOld = Math.floor((Date.now() - new Date(vars.receipt_date || Date.now()).getTime()) / 86400000);
if (daysOld > 90) violations.push("Receipt older than 90 days (" + daysOld + " days)");
const needsManagerApproval = amount > 50 || violations.length > 0;
return {
  policy_violations: violations.join("; ") || "none",
  violation_count: violations.length,
  passes_policy: violations.length === 0 ? "yes" : "no",
  category_limit: limit,
  needs_manager_approval: needsManagerApproval ? "yes" : "no",
  receipt_age_days: daysOld,
  approval_required_reason: needsManagerApproval ? (amount > 50 ? "Amount over $50" : "Policy violation") : "auto-approved"
};`,
          outputVariables: [
            { name: "policy_violations", type: "string" },
            { name: "violation_count", type: "number" },
            { name: "passes_policy", type: "string" },
            { name: "category_limit", type: "number" },
            { name: "needs_manager_approval", type: "string" },
            { name: "receipt_age_days", type: "number" },
            { name: "approval_required_reason", type: "string" },
          ],
        },
      }),

      // 4. Violation routing condition
      n("4", "condition", "Passes policy (no violations)?", 760, {
        conditionData: [
          {
            variable: "passes_policy",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Manager approval (Slack)
      slackNode(
        "5",
        "Request manager approval (Slack)",
        1000,
        "Reimbursement Approval Request:\\n\\nID: {reimbursement_id}\\nEmployee: {employee_name} ({department})\\nAmount: {receipt_currency} {receipt_amount}\\nVendor: {receipt_vendor}\\nCategory: {expense_category}\\nPurpose: {business_purpose}\\nDate: {receipt_date}\\nReceipt Valid: {receipt_valid}\\n\\nPolicy: {approval_required_reason}\\nViolations: {policy_violations}\\n\\nPlease approve or reject in the expense portal.",
      ),

      // 6. Accounting write
      sheetsNode(
        "6",
        "Write accounting entry (Sheets)",
        1240,
        "AccountingEntries",
        '[["{reimbursement_id}","{employee_name}","{department}","{receipt_vendor}","{receipt_amount}","{receipt_currency}","{expense_category}","{receipt_date}","pending_payment"]]',
      ),

      // 7. Payroll queue
      sheetsNode(
        "7",
        "Queue for payroll (Sheets)",
        1480,
        "PayrollQueue",
        '[["{reimbursement_id}","{employee_email}","{employee_name}","{receipt_amount}","{receipt_currency}","reimbursement","queued"]]',
      ),

      // 8. Employee stage notifications (email)
      emailNode(
        "8",
        "Notify employee of status",
        1720,
        "{employee_email}",
        "Reimbursement Update: {reimbursement_id} - {receipt_currency} {receipt_amount}",
        "Hi {employee_name},\\n\\nYour reimbursement request has been processed.\\n\\nID: {reimbursement_id}\\nAmount: {receipt_currency} {receipt_amount}\\nVendor: {receipt_vendor}\\nCategory: {expense_category}\\nPolicy Status: {passes_policy}\\nViolations: {policy_violations}\\n\\nYour reimbursement has been queued for the next payroll cycle. You will receive the amount in your regular paycheck.\\n\\nIf you have questions, contact accounting@company.com.",
      ),

      // 9. Audit logging (Sheets)
      sheetsNode(
        "9",
        "Log audit trail (Sheets)",
        1960,
        "ReimbursementAudit",
        '[["{reimbursement_id}","{employee_name}","{receipt_amount}","{expense_category}","{passes_policy}","{policy_violations}","{needs_manager_approval}","{receipt_age_days}","processed"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // passes policy -> still get manager approval for >$50
      e("4", "5", "output_2"), // violations -> definitely needs approval
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 712. EMPLOYEE RESOURCE HUB CHATBOT (7 nodes)
  // ===================================================================

  "Employee Resource Hub Chatbot": {
    summary:
      "AI-powered HR resource chatbot with RAG-based handbook search. An HR question triggers the flow, Pinecone retrieves relevant handbook sections, AI generates a contextual answer, a confidence condition gates low-confidence responses, sensitive topics are escalated to HR, topic demand is logged in Sheets, and self-service resource bundles are delivered.",
    nodes: [
      trigger("Webhook: HR question received", "webhook_app", {
        source: "Slack / Internal Portal Chat",
        event: "chat.hr_question",
        howItStarts:
          "An employee asks an HR-related question via the internal chat widget or Slack HR bot",
        setup:
          "Configure the HR chat widget or Slack bot to POST employee questions to this workflow URL. Payload includes question text, employee identity, and conversation context.",
        inputFields: [],
      }),

      // 2. RAG handbook search (Pinecone)
      aiNode(
        "2",
        "AI: Extract question intent",
        280,
        "You are an HR question classifier. Parse the employee question and determine the intent, topic category, sensitivity level, and search terms for handbook lookup. Respond JSON: {employee_name: string, employee_email: string, question_text: string, topic_category: string, search_terms: string, sensitivity_level: string, conversation_id: string, is_sensitive: string}",
        "HR question payload: {webhook_payload}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "question_text", as: "question_text", type: "string" },
          { field: "topic_category", as: "topic_category", type: "string" },
          { field: "search_terms", as: "search_terms", type: "string" },
          {
            field: "sensitivity_level",
            as: "sensitivity_level",
            type: "string",
          },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "is_sensitive", as: "is_sensitive", type: "string" },
        ],
      ),

      // 3. Pinecone search
      n("3", "hitApi", "Pinecone: Search employee handbook", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_index_host}/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vector", value: "{search_embedding}" },
            { key: "topK", value: "5" },
            { key: "includeMetadata", value: "true" },
            { key: "namespace", value: "employee-handbook" },
          ],
          variables: [
            {
              name: "matches[0].metadata.text",
              customName: "handbook_section_1",
              type: "string",
            },
            {
              name: "matches[1].metadata.text",
              customName: "handbook_section_2",
              type: "string",
            },
            {
              name: "matches[0].score",
              customName: "relevance_score",
              type: "number",
            },
            {
              name: "matches[0].metadata.section",
              customName: "source_section",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI contextual answer
      aiNode(
        "4",
        "AI: Generate contextual HR answer",
        760,
        "You are a helpful HR knowledge assistant. Using the handbook sections provided, answer the employee question accurately and helpfully. Cite specific handbook sections. If the handbook does not cover the topic, say so clearly. Never guess about policy details. Respond JSON: {answer_text: string, source_citation: string, confidence_score: number, confidence_level: string, follow_up_resources: string}",
        "Question: {question_text}. Handbook context: Section 1: {handbook_section_1}. Section 2: {handbook_section_2}. Source: {source_section}. Relevance: {relevance_score}",
        [
          { field: "answer_text", as: "answer_text", type: "string" },
          { field: "source_citation", as: "source_citation", type: "string" },
          { field: "confidence_score", as: "confidence_score", type: "number" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          {
            field: "follow_up_resources",
            as: "follow_up_resources",
            type: "string",
          },
        ],
      ),

      // 5. Confidence condition
      n("5", "condition", "High confidence & not sensitive?", 1000, {
        conditionData: [
          {
            variable: "is_sensitive",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Topic demand logging (Sheets)
      sheetsNode(
        "6",
        "Log topic demand (Sheets)",
        1240,
        "HRTopicDemand",
        '[["{employee_email}","{topic_category}","{question_text}","{confidence_level}","{sensitivity_level}","{source_section}"]]',
      ),

      // 7. Self-service resource bundles (sendMessage)
      n("7", "sendMessage", "Deliver answer to employee", 1480, {
        sendMessageDetails: {
          conversationId: "{conversation_id}",
          message:
            "{answer_text}\\n\\nSource: {source_citation}\\n\\nAdditional resources: {follow_up_resources}",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // confident & not sensitive -> deliver answer
      e("5", "6", "output_2"), // sensitive or low confidence -> log & escalate
      e("7", "6"),
    ],
  },

  // ===================================================================
  // 713. EMPLOYEE SATISFACTION SURVEY (8 nodes)
  // ===================================================================

  "Employee Satisfaction Survey": {
    summary:
      "Quarterly employee satisfaction survey with secure distribution, conversational format, anonymized storage, AI analysis, reminders, and manager-safe reporting. A quarterly trigger fires, secure survey links are distributed via email, AI generates a conversational survey, responses are anonymized, AI analyzes themes, completion reminders are sent, executives receive a report, and a condition ensures manager-safe aggregation.",
    nodes: [
      trigger("Schedule: Quarterly satisfaction survey", "schedule", {
        schedule: "Quarterly (first Monday of Jan, Apr, Jul, Oct)",
        howItStarts:
          "Runs quarterly to distribute employee satisfaction surveys with full anonymization",
        setup:
          "Activate the workflow. Surveys are fully anonymized with no individual tracking. Configure email distribution list and OpenAI key for analysis.",
        inputFields: [],
      }),

      // 2. Secure link distribution (email)
      n("2", "runCode", "Generate secure survey batch", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate secure, anonymized survey batch
const quarter = "Q" + Math.ceil((new Date().getMonth() + 1) / 3);
const year = new Date().getFullYear();
const batchId = "SAT-" + quarter + "-" + year + "-" + Math.random().toString(36).substring(2, 8);
return {
  batch_id: batchId,
  survey_quarter: quarter + " " + year,
  survey_url: "https://surveys.company.com/satisfaction/" + batchId,
  distribution_email: "all-employees@company.com",
  deadline_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
  anonymity_guarantee: "Responses are fully anonymous. Minimum 5 responses per group before results are shown."
};`,
          outputVariables: [
            { name: "batch_id", type: "string" },
            { name: "survey_quarter", type: "string" },
            { name: "survey_url", type: "string" },
            { name: "distribution_email", type: "string" },
            { name: "deadline_date", type: "string" },
            { name: "anonymity_guarantee", type: "string" },
          ],
        },
      }),

      // 3. Distribution email
      emailNode(
        "3",
        "Distribute satisfaction survey",
        520,
        "{distribution_email}",
        "Your Voice Matters: {survey_quarter} Employee Satisfaction Survey",
        "Hi team,\\n\\nIt is time for our {survey_quarter} Employee Satisfaction Survey!\\n\\nYour honest feedback helps us build a better workplace.\\n\\nSurvey: {survey_url}\\nDeadline: {deadline_date}\\nTime: ~10 minutes\\n\\nPrivacy: {anonymity_guarantee}\\n\\nTopics covered: work environment, leadership, growth, compensation, work-life balance, and overall satisfaction.\\n\\nThank you for sharing your perspective!\\n\\nHR Team",
      ),

      // 4. Conversational survey (AI)
      aiNode(
        "4",
        "AI: Design conversational survey",
        760,
        "You are an employee survey design expert. Create a conversational, engaging satisfaction survey covering: overall satisfaction, work environment, leadership effectiveness, career growth, compensation fairness, work-life balance, and team dynamics. Mix rating scales with open-ended questions. Respond JSON: {survey_sections: string, total_questions: number, open_ended_count: number, rating_count: number, estimated_minutes: number, engagement_hooks: string}",
        "Quarter: {survey_quarter}, Batch: {batch_id}. Design an engaging yet thorough survey that employees will want to complete.",
        [
          { field: "survey_sections", as: "survey_sections", type: "string" },
          { field: "total_questions", as: "total_questions", type: "number" },
          { field: "open_ended_count", as: "open_ended_count", type: "number" },
          { field: "rating_count", as: "rating_count", type: "number" },
          {
            field: "estimated_minutes",
            as: "estimated_minutes",
            type: "number",
          },
          { field: "engagement_hooks", as: "engagement_hooks", type: "string" },
        ],
      ),

      // 5. Anonymized storage + theme analysis (AI)
      aiNode(
        "5",
        "AI: Prepare analysis framework",
        1000,
        "You are a people analytics expert. Design the analysis framework for the satisfaction survey results. Define theme categories, scoring methodology, comparison benchmarks, and reporting structure. Ensure all reports protect anonymity (minimum group size of 5). Respond JSON: {theme_categories: string, scoring_methodology: string, benchmark_targets: string, anonymity_rules: string, report_structure: string, manager_safe: string}",
        "Survey: {survey_quarter}, Sections: {survey_sections}, Total questions: {total_questions}, Rating questions: {rating_count}, Open-ended: {open_ended_count}",
        [
          { field: "theme_categories", as: "theme_categories", type: "string" },
          {
            field: "scoring_methodology",
            as: "scoring_methodology",
            type: "string",
          },
          {
            field: "benchmark_targets",
            as: "benchmark_targets",
            type: "string",
          },
          { field: "anonymity_rules", as: "anonymity_rules", type: "string" },
          { field: "report_structure", as: "report_structure", type: "string" },
          { field: "manager_safe", as: "manager_safe", type: "string" },
        ],
      ),

      // 6. Completion reminders (delay + email)
      n("6", "delayAction", "Wait 10 days for responses", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "10",
          delayUnit: "days",
        },
      }),

      // 7. Executive report (email)
      emailNode(
        "7",
        "Send executive survey report",
        1480,
        "leadership@company.com",
        "Employee Satisfaction Survey: {survey_quarter} - Framework Ready",
        "Employee Satisfaction Survey Report\\n\\nQuarter: {survey_quarter}\\nBatch: {batch_id}\\nDeadline: {deadline_date}\\n\\nSurvey Design:\\n- Total Questions: {total_questions}\\n- Rating Questions: {rating_count}\\n- Open-Ended: {open_ended_count}\\n- Est. Completion: {estimated_minutes} minutes\\n\\nAnalysis Framework:\\n- Themes: {theme_categories}\\n- Scoring: {scoring_methodology}\\n- Benchmarks: {benchmark_targets}\\n\\nAnonymity: {anonymity_rules}\\nReport Structure: {report_structure}\\n\\nResults will be available after the deadline once minimum response thresholds are met.",
      ),

      // 8. Manager-safe report condition
      n("8", "condition", "Manager reports safe (min 5 responses)?", 1720, {
        conditionData: [
          {
            variable: "manager_safe",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 714. EMPLOYEE SKILLS INVENTORY BOT (7 nodes)
  // ===================================================================

  "Employee Skills Inventory Bot": {
    summary:
      "Conversational bot for managing the employee skills inventory. A chat trigger starts the flow, AI classifies the intent (query, update, analyze), the skills store is queried, profile update guidance is provided, AI performs team gap analysis, queries are logged in Sheets, and expert recommendations are delivered.",
    nodes: [
      trigger("Webhook: Skills inventory chat", "webhook_app", {
        source: "Slack / Internal Portal",
        event: "chat.skills.query",
        howItStarts:
          "An employee or manager sends a skills-related query via Slack or the internal skills portal",
        setup:
          "Configure a Slack bot or portal widget to POST skills queries to this workflow URL. Payload includes the user message and identity.",
        inputFields: [],
      }),

      // 2. AI intent classification
      aiNode(
        "2",
        "AI: Classify skills intent",
        280,
        "You are a skills inventory assistant. Parse the message to determine the user intent: query (searching for people with skills), update (updating their own skills), or analyze (team gap analysis). Extract relevant details. Respond JSON: {user_name: string, user_email: string, intent: string, skill_query: string, skills_to_update: string, team_name: string, conversation_id: string, search_keywords: string}",
        "Skills query payload: {webhook_payload}",
        [
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "intent", as: "intent", type: "string" },
          { field: "skill_query", as: "skill_query", type: "string" },
          { field: "skills_to_update", as: "skills_to_update", type: "string" },
          { field: "team_name", as: "team_name", type: "string" },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "search_keywords", as: "search_keywords", type: "string" },
        ],
      ),

      // 3. Skills store query
      n("3", "hitApi", "Query skills inventory (Sheets API)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/SkillsInventory!A1:Z1000",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "skills_data", type: "string" },
            { name: "range", customName: "data_range", type: "string" },
          ],
        },
      }),

      // 4. Profile update guidance (AI)
      aiNode(
        "4",
        "AI: Process skills request",
        760,
        "You are a skills inventory manager. Based on the user intent and skills data, provide the appropriate response: for queries, find matching employees; for updates, guide the user through updating their profile; for analysis, identify skill gaps. Respond JSON: {response_message: string, matching_experts: string, skill_gaps: string, recommendations: string, action_items: string, expert_count: number}",
        "User: {user_name}, Intent: {intent}, Query: {skill_query}, Team: {team_name}, Skills to update: {skills_to_update}, Search: {search_keywords}, Current inventory: {skills_data}",
        [
          { field: "response_message", as: "response_message", type: "string" },
          { field: "matching_experts", as: "matching_experts", type: "string" },
          { field: "skill_gaps", as: "skill_gaps", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "expert_count", as: "expert_count", type: "number" },
        ],
      ),

      // 5. AI team gap analysis
      aiNode(
        "5",
        "AI: Generate team gap analysis",
        1000,
        "You are a workforce planning specialist. Analyze the skills data and identify team-level skill gaps, upcoming needs, and training recommendations. Respond JSON: {gap_summary: string, critical_gaps: string, training_recommendations: string, hiring_recommendations: string, strengths: string}",
        "Team: {team_name}, Current skills data: {skills_data}, Gaps identified: {skill_gaps}, Expert count: {expert_count}",
        [
          { field: "gap_summary", as: "gap_summary", type: "string" },
          { field: "critical_gaps", as: "critical_gaps", type: "string" },
          {
            field: "training_recommendations",
            as: "training_recommendations",
            type: "string",
          },
          {
            field: "hiring_recommendations",
            as: "hiring_recommendations",
            type: "string",
          },
          { field: "strengths", as: "strengths", type: "string" },
        ],
      ),

      // 6. Query logging (Sheets)
      sheetsNode(
        "6",
        "Log skills query (Sheets)",
        1240,
        "SkillsQueryLog",
        '[["{user_name}","{user_email}","{intent}","{skill_query}","{team_name}","{expert_count}","{search_keywords}"]]',
      ),

      // 7. Expert recommendations (sendMessage)
      n("7", "sendMessage", "Deliver skills response", 1480, {
        sendMessageDetails: {
          conversationId: "{conversation_id}",
          message:
            "{response_message}\\n\\nExperts: {matching_experts}\\nRecommendations: {recommendations}\\nAction items: {action_items}",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 715. EMPLOYEE WELLNESS CHECK-IN (8 nodes)
  // ===================================================================

  "Employee Wellness Check-In": {
    summary:
      "Monthly wellness check-in with empathetic AI conversations, crisis detection, anonymized metrics, and tailored resources. A monthly trigger fires, channel preference routing selects delivery, AI conducts an empathetic wellness conversation, a crisis condition detects distress signals, human escalation goes to Slack, anonymized metrics are stored in Sheets, manager-safe trends are generated, and tailored resources are emailed.",
    nodes: [
      trigger("Schedule: Monthly wellness check-in", "schedule", {
        schedule: "Monthly (first Tuesday at 10:00 AM)",
        howItStarts:
          "Runs monthly to initiate confidential wellness check-in conversations with employees",
        setup:
          "Activate the workflow. All wellness data is anonymized. Configure crisis escalation contacts in Slack. Ensure counseling resources links are current.",
        inputFields: [],
      }),

      // 2. Channel preference routing
      n("2", "runCode", "Configure wellness distribution", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Configure wellness check-in distribution
const month = new Date().toLocaleString("default", { month: "long" });
const year = new Date().getFullYear();
const batchId = "WELL-" + month.substring(0,3).toUpperCase() + "-" + year;
return {
  batch_id: batchId,
  check_in_month: month + " " + year,
  distribution_email: "all-employees@company.com",
  wellness_url: "https://wellness.company.com/checkin/" + batchId,
  crisis_hotline: "1-800-273-8255",
  eap_link: "https://eap.company.com",
  anonymity_notice: "This check-in is completely confidential. Your responses are anonymized and cannot be linked to you."
};`,
          outputVariables: [
            { name: "batch_id", type: "string" },
            { name: "check_in_month", type: "string" },
            { name: "distribution_email", type: "string" },
            { name: "wellness_url", type: "string" },
            { name: "crisis_hotline", type: "string" },
            { name: "eap_link", type: "string" },
            { name: "anonymity_notice", type: "string" },
          ],
        },
      }),

      // 3. Empathetic wellness conversation (AI)
      aiNode(
        "3",
        "AI: Design wellness conversation",
        520,
        "You are a compassionate workplace wellness specialist. Design a brief, empathetic wellness check-in conversation covering: overall wellbeing (1-5), stress level, work-life balance, support satisfaction, and an open-ended reflection. Include crisis detection signals (keywords: hopeless, overwhelmed, harm, crisis, breaking point). Respond JSON: {conversation_flow: string, wellness_questions: string, crisis_keywords: string, resource_categories: string, estimated_minutes: number, crisis_detected: string}",
        "Month: {check_in_month}, Batch: {batch_id}. Design a warm, non-invasive check-in that employees will feel comfortable completing.",
        [
          {
            field: "conversation_flow",
            as: "conversation_flow",
            type: "string",
          },
          {
            field: "wellness_questions",
            as: "wellness_questions",
            type: "string",
          },
          { field: "crisis_keywords", as: "crisis_keywords", type: "string" },
          {
            field: "resource_categories",
            as: "resource_categories",
            type: "string",
          },
          {
            field: "estimated_minutes",
            as: "estimated_minutes",
            type: "number",
          },
          { field: "crisis_detected", as: "crisis_detected", type: "string" },
        ],
      ),

      // 4. Crisis signal condition
      n("4", "condition", "Crisis signal detected?", 760, {
        conditionData: [
          {
            variable: "crisis_detected",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Human wellness escalation (Slack)
      slackNode(
        "5",
        "URGENT: Wellness crisis escalation (Slack)",
        1000,
        "CONFIDENTIAL WELLNESS ALERT:\\n\\nA wellness check-in response has triggered crisis detection signals.\\nBatch: {batch_id} | Month: {check_in_month}\\n\\nNote: Response is ANONYMIZED - no individual can be identified.\\n\\nCrisis keywords detected. Please:\\n1. Review EAP resources availability\\n2. Send a company-wide reminder about mental health resources\\n3. Ensure crisis hotline ({crisis_hotline}) is prominently displayed\\n\\nEAP: {eap_link}",
      ),

      // 6. Anonymized metrics (Sheets)
      sheetsNode(
        "6",
        "Store anonymized metrics (Sheets)",
        1240,
        "WellnessMetrics",
        '[["{batch_id}","{check_in_month}","{estimated_minutes}","{resource_categories}","{crisis_detected}","batch_processed"]]',
      ),

      // 7. Manager-safe trends
      aiNode(
        "7",
        "AI: Generate manager-safe trends",
        1480,
        "You are a workplace wellness analyst. Generate manager-safe aggregate wellness insights that protect individual anonymity. Provide trends, resource recommendations, and team-level action items. Never include anything that could identify individuals. Respond JSON: {trend_summary: string, resource_recommendations: string, action_items: string, overall_wellness_indicator: string}",
        "Batch: {batch_id}, Month: {check_in_month}, Resource categories: {resource_categories}, Crisis detected: {crisis_detected}",
        [
          { field: "trend_summary", as: "trend_summary", type: "string" },
          {
            field: "resource_recommendations",
            as: "resource_recommendations",
            type: "string",
          },
          { field: "action_items", as: "action_items", type: "string" },
          {
            field: "overall_wellness_indicator",
            as: "overall_wellness_indicator",
            type: "string",
          },
        ],
      ),

      // 8. Tailored resources email
      emailNode(
        "8",
        "Send tailored wellness resources",
        1720,
        "{distribution_email}",
        "Wellness Resources: {check_in_month}",
        "Hi team,\\n\\nThank you for participating in this month's wellness check-in.\\n\\nBased on aggregate (anonymized) trends, here are resources that may be helpful:\\n\\n{resource_recommendations}\\n\\nRemember:\\n- EAP (free confidential counseling): {eap_link}\\n- Crisis Hotline: {crisis_hotline}\\n- Manager Support: Talk to your manager about workload concerns\\n\\nWellness is a priority. Please take advantage of these resources.\\n\\n{anonymity_notice}\\n\\nHR Wellness Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // crisis -> escalate
      e("4", "6", "output_2"), // no crisis -> log metrics
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 716. EXIT INTERVIEW BOT (8 nodes)
  // ===================================================================

  "Exit Interview Bot": {
    summary:
      "Structured exit interview workflow with AI-conducted interviews, sentiment analysis, identity stripping, and systemic signal detection. A final-week trigger fires, an interview invitation is sent, AI conducts a structured interview, sentiment and themes are analyzed, identity is stripped via runCode, themes are stored in Sheets, HR receives a summary email, and systemic signals trigger escalation via condition.",
    nodes: [
      trigger("Webhook: Final week departing employee", "webhook_app", {
        source: "BambooHR / HR System",
        event: "employee.final_week",
        howItStarts:
          "The HRIS triggers a webhook when a departing employee enters their final week, initiating the exit interview process",
        setup:
          "Configure BambooHR to fire a webhook when an employee reaches their last week. Payload includes employee details and departure information.",
        inputFields: [],
      }),

      // 2. AI extraction + interview invitation
      aiNode(
        "2",
        "AI: Parse departure & prepare interview",
        280,
        "You are an exit interview coordinator. Parse the final-week webhook to extract employee details and prepare the exit interview framework. Respond JSON: {emp_name: string, emp_email: string, department: string, role: string, tenure_years: number, departure_reason: string, manager_name: string, last_day: string, interview_url: string}",
        "Final week webhook payload: {webhook_payload}",
        [
          { field: "emp_name", as: "emp_name", type: "string" },
          { field: "emp_email", as: "emp_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "role", as: "role", type: "string" },
          { field: "tenure_years", as: "tenure_years", type: "number" },
          { field: "departure_reason", as: "departure_reason", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "last_day", as: "last_day", type: "string" },
          { field: "interview_url", as: "interview_url", type: "string" },
        ],
      ),

      // 3. Interview invitation
      emailNode(
        "3",
        "Send exit interview invitation",
        520,
        "{emp_email}",
        "We'd Love Your Feedback: Exit Interview - {emp_name}",
        "Hi {emp_name},\\n\\nAs you prepare for your last day ({last_day}), we would greatly value your honest feedback about your experience.\\n\\nPlease complete our confidential exit interview:\\n{interview_url}\\n\\nThe interview covers your experience with your role, team, management, company culture, and suggestions for improvement.\\n\\nAll feedback is kept confidential and used only in aggregate to improve the employee experience.\\n\\nTime: ~15 minutes\\n\\nThank you for your contributions during your {tenure_years} years with us.\\n\\nHR Team",
      ),

      // 4. AI structured interview
      aiNode(
        "4",
        "AI: Conduct structured exit interview",
        760,
        "You are an empathetic exit interview specialist. Design a structured exit interview covering: overall experience, reasons for leaving, management effectiveness, growth opportunities, compensation satisfaction, company culture, what they would change, and what they would keep. Generate probing follow-up questions. Respond JSON: {interview_sections: string, total_questions: number, probing_questions: string, key_areas: string, sentiment_indicators: string}",
        "Employee: {emp_name}, Role: {role}, Department: {department}, Tenure: {tenure_years} years, Departure reason: {departure_reason}, Manager: {manager_name}",
        [
          {
            field: "interview_sections",
            as: "interview_sections",
            type: "string",
          },
          { field: "total_questions", as: "total_questions", type: "number" },
          {
            field: "probing_questions",
            as: "probing_questions",
            type: "string",
          },
          { field: "key_areas", as: "key_areas", type: "string" },
          {
            field: "sentiment_indicators",
            as: "sentiment_indicators",
            type: "string",
          },
        ],
      ),

      // 5. Identity stripping (runCode)
      n("5", "runCode", "Strip PII for anonymized analysis", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Strip all personally identifiable information for anonymized storage
const anonId = "EXIT-" + Date.now().toString(36) + Math.random().toString(36).substring(2, 6);
const deptBucket = vars.department || "Unknown";
const tenureBucket = parseInt(vars.tenure_years) || 0;
const tenureRange = tenureBucket < 1 ? "<1yr" : tenureBucket < 3 ? "1-3yr" : tenureBucket < 5 ? "3-5yr" : "5+yr";
const reasonCategory = (vars.departure_reason || "other").toLowerCase();
const isVoluntary = reasonCategory.includes("voluntary") || reasonCategory.includes("new opportunity") || reasonCategory.includes("personal");
return {
  anon_id: anonId,
  dept_bucket: deptBucket,
  tenure_range: tenureRange,
  reason_category: reasonCategory,
  is_voluntary: isVoluntary ? "yes" : "no",
  identity_stripped: "yes",
  pii_removed_fields: "name, email, manager_name stripped"
};`,
          outputVariables: [
            { name: "anon_id", type: "string" },
            { name: "dept_bucket", type: "string" },
            { name: "tenure_range", type: "string" },
            { name: "reason_category", type: "string" },
            { name: "is_voluntary", type: "string" },
            { name: "identity_stripped", type: "string" },
            { name: "pii_removed_fields", type: "string" },
          ],
        },
      }),

      // 6. Exit theme database (Sheets)
      sheetsNode(
        "6",
        "Store anonymized exit themes (Sheets)",
        1240,
        "ExitInterviewThemes",
        '[["{anon_id}","{dept_bucket}","{tenure_range}","{reason_category}","{is_voluntary}","{key_areas}","{sentiment_indicators}","{total_questions}"]]',
      ),

      // 7. HR summary email
      emailNode(
        "7",
        "Send HR exit interview summary",
        1480,
        "hr-analytics@company.com",
        "Exit Interview Complete: {dept_bucket} - {tenure_range} tenure",
        "Exit Interview Summary (Anonymized)\\n\\nAnonymous ID: {anon_id}\\nDepartment: {dept_bucket}\\nTenure: {tenure_range}\\nDeparture Category: {reason_category}\\nVoluntary: {is_voluntary}\\n\\nKey Areas Covered: {key_areas}\\nSentiment Indicators: {sentiment_indicators}\\nInterview Sections: {interview_sections}\\n\\nPII Status: {pii_removed_fields}\\n\\nFull anonymized responses available in the Exit Interview Themes dashboard.",
      ),

      // 8. Systemic signal escalation condition
      n("8", "condition", "Systemic concern detected?", 1720, {
        conditionData: [
          {
            variable: "is_voluntary",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 717. EXIT INTERVIEW CHATBOT (8 nodes)
  // ===================================================================

  "Exit Interview Chatbot": {
    summary:
      "Conversational exit interview chatbot with branching questions, theme categorization, anonymized insights, trend tracking, and confidentiality protections. A late offboarding trigger fires, AI conducts a branching interview, respectful follow-ups probe deeper, themes are categorized, an anonymized insight packet is created, trends are tracked in a dashboard via Sheets, recurring concerns trigger Slack escalation, and a confidentiality condition ensures data protection.",
    nodes: [
      trigger("Webhook: Late offboarding exit interview", "webhook_app", {
        source: "HR Offboarding System",
        event: "offboarding.exit_interview_phase",
        howItStarts:
          "The offboarding workflow reaches the exit interview phase in the final days, triggering the conversational chatbot",
        setup:
          "Configure the offboarding workflow to fire this webhook when the exit interview phase is reached. Payload includes departing employee context.",
        inputFields: [],
      }),

      // 2. Branching interview (AI)
      aiNode(
        "2",
        "AI: Conduct branching exit interview",
        280,
        "You are a compassionate exit interview chatbot. Parse the offboarding context and conduct a thoughtful, branching interview. Start with overall sentiment, then branch based on responses: positive experiences -> what to keep; negative -> what to improve; neutral -> deeper probing. Respond JSON: {emp_name: string, emp_email: string, department: string, tenure_years: number, role: string, departure_reason: string, initial_sentiment: string, conversation_id: string, interview_started: string, branch_path: string}",
        "Exit interview context: {webhook_payload}",
        [
          { field: "emp_name", as: "emp_name", type: "string" },
          { field: "emp_email", as: "emp_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "tenure_years", as: "tenure_years", type: "number" },
          { field: "role", as: "role", type: "string" },
          { field: "departure_reason", as: "departure_reason", type: "string" },
          {
            field: "initial_sentiment",
            as: "initial_sentiment",
            type: "string",
          },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          {
            field: "interview_started",
            as: "interview_started",
            type: "string",
          },
          { field: "branch_path", as: "branch_path", type: "string" },
        ],
      ),

      // 3. Respectful follow-ups (AI)
      aiNode(
        "3",
        "AI: Generate respectful follow-ups",
        520,
        "You are a skilled exit interviewer. Based on the initial sentiment and branch path, generate respectful, probing follow-up questions. For negative sentiment: ask about specific incidents without being pushy. For positive: ask what made it great. Always validate feelings and thank for honesty. Respond JSON: {follow_up_questions: string, emotional_tone: string, themes_emerging: string, management_feedback: string, culture_feedback: string, growth_feedback: string, compensation_feedback: string, overall_recommendation: string}",
        "Employee: {emp_name}, Department: {department}, Tenure: {tenure_years} years, Role: {role}, Reason: {departure_reason}, Sentiment: {initial_sentiment}, Branch: {branch_path}",
        [
          {
            field: "follow_up_questions",
            as: "follow_up_questions",
            type: "string",
          },
          { field: "emotional_tone", as: "emotional_tone", type: "string" },
          { field: "themes_emerging", as: "themes_emerging", type: "string" },
          {
            field: "management_feedback",
            as: "management_feedback",
            type: "string",
          },
          { field: "culture_feedback", as: "culture_feedback", type: "string" },
          { field: "growth_feedback", as: "growth_feedback", type: "string" },
          {
            field: "compensation_feedback",
            as: "compensation_feedback",
            type: "string",
          },
          {
            field: "overall_recommendation",
            as: "overall_recommendation",
            type: "string",
          },
        ],
      ),

      // 4. Theme categorization (AI)
      aiNode(
        "4",
        "AI: Categorize exit themes",
        760,
        "You are an exit interview analyst. Categorize all feedback into actionable themes. Identify recurring patterns, severity, and department-specific issues. Flag any themes that appear systemic. Respond JSON: {primary_themes: string, secondary_themes: string, systemic_flags: string, severity_level: string, has_recurring_concern: string, anonymized_summary: string, actionable_insights: string}",
        "Department: {department}, Sentiment: {initial_sentiment}, Tone: {emotional_tone}, Themes: {themes_emerging}, Management: {management_feedback}, Culture: {culture_feedback}, Growth: {growth_feedback}, Compensation: {compensation_feedback}",
        [
          { field: "primary_themes", as: "primary_themes", type: "string" },
          { field: "secondary_themes", as: "secondary_themes", type: "string" },
          { field: "systemic_flags", as: "systemic_flags", type: "string" },
          { field: "severity_level", as: "severity_level", type: "string" },
          {
            field: "has_recurring_concern",
            as: "has_recurring_concern",
            type: "string",
          },
          {
            field: "anonymized_summary",
            as: "anonymized_summary",
            type: "string",
          },
          {
            field: "actionable_insights",
            as: "actionable_insights",
            type: "string",
          },
        ],
      ),

      // 5. Anonymized insight packet (runCode)
      n("5", "runCode", "Create anonymized insight packet", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Create fully anonymized insight packet - strip all PII
const anonId = "CHAT-EXIT-" + Date.now().toString(36) + Math.random().toString(36).substring(2, 6);
const dept = vars.department || "Unknown";
const tenure = parseInt(vars.tenure_years) || 0;
const tenureBand = tenure < 1 ? "<1yr" : tenure < 3 ? "1-3yr" : tenure < 5 ? "3-5yr" : "5+yr";
const sentiment = vars.initial_sentiment || "neutral";
const severity = vars.severity_level || "low";
const isConfidential = true;
return {
  anon_packet_id: anonId,
  dept_category: dept,
  tenure_band: tenureBand,
  sentiment_label: sentiment,
  severity_label: severity,
  confidentiality_verified: "yes",
  pii_status: "all PII removed - name, email, role stripped",
  packet_ready: "yes"
};`,
          outputVariables: [
            { name: "anon_packet_id", type: "string" },
            { name: "dept_category", type: "string" },
            { name: "tenure_band", type: "string" },
            { name: "sentiment_label", type: "string" },
            { name: "severity_label", type: "string" },
            { name: "confidentiality_verified", type: "string" },
            { name: "pii_status", type: "string" },
            { name: "packet_ready", type: "string" },
          ],
        },
      }),

      // 6. Trend dashboard (Sheets)
      sheetsNode(
        "6",
        "Update trend dashboard (Sheets)",
        1240,
        "ExitInterviewTrends",
        '[["{anon_packet_id}","{dept_category}","{tenure_band}","{sentiment_label}","{severity_label}","{primary_themes}","{systemic_flags}","{has_recurring_concern}","{confidentiality_verified}"]]',
      ),

      // 7. Recurring concern escalation (Slack)
      slackNode(
        "7",
        "Escalate recurring concern (Slack)",
        1480,
        "EXIT INTERVIEW INSIGHT (Anonymized):\\n\\nPacket: {anon_packet_id}\\nDepartment: {dept_category}\\nTenure: {tenure_band}\\nSentiment: {sentiment_label}\\nSeverity: {severity_label}\\n\\nPrimary Themes: {primary_themes}\\nSystemic Flags: {systemic_flags}\\nRecurring: {has_recurring_concern}\\n\\nActionable Insights: {actionable_insights}\\n\\nConfidentiality: {pii_status}",
      ),

      // 8. Confidentiality protection condition
      n("8", "condition", "Confidentiality verified?", 1720, {
        conditionData: [
          {
            variable: "confidentiality_verified",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
